import { useState, useEffect, useRef, useCallback } from "react";
import { safeHref } from "./utils/safeUrl";
import { resolveStartNode, storedRoute, validateRoute, splitLegByFloor, isOutdoorFloor, EITC_CENTER, START_LABEL } from "./utils/engtourRoutes";
import { introContent, parseGuide, fillStamps, checkAnswers, isAsked } from "./utils/engtourIntro.js";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_KEY;
const API_BASE = process.env.NODE_ENV === "development" ? "http://localhost:3001" : "";

// Include outdoors so cross-building legs can route through campus paths
// ⚠️ `nav_tunnels` carries the INDOOR links between E1, E2, E3 and the Stanley
// Pauley buildings (atrium crossings, the E1 bridges) — not the building files.
// Leaving it out sent every between-building leg out of one exit and in
// through another. See the NAV_KEYS note in EngTourCreator.js.
const EITC_NAV_KEYS = ['nav_eitc_e1', 'nav_eitc_e2', 'nav_eitc_e3', 'nav_stanley_pauley_centre', 'nav_stanley_pauley_engineering', 'nav_outdoors', 'nav_tunnels'];
const BUILDING_KEYS = [
  { key: 'eitc_e1', building: 'e1' },
  { key: 'eitc_e2', building: 'e2' },
  { key: 'eitc_e3', building: 'e3' },
  { key: 'stanley_pauley_centre', building: 'stanley_pauley_centre' },
  { key: 'stanley_pauley_engineering', building: 'stanley_pauley_engineering' },
];

// Engineering departments a stop / free-roam place can be tagged with. Drives
// the Free Roam filter chips: a student picks a department to see only the
// places relevant to it. Untagged places show under "All" only.
const DEPARTMENTS = ["Mechanical", "Civil", "Electrical", "Computer", "Biosystems"];

// Preview mode (the Tour Creator's "Test tour"): the draft data still carries
// raw quote answers (the live API strips them). Compute the `hasQuote` flag the
// UI expects, keeping `quote` so the door-quote gate can be checked client-side.
// Everything else mirrors `sanitizeForClient` in api/_tourData.js so the
// preview walks the same document a visitor gets: top-level fields such as
// `welcome` pass straight through, and the free-roam places are derived from
// the starred `stopBank` entries — the Creator folds its `freeRoamLocations`
// into the bank, so without this the Free Roam a preview reaches after the
// completion screen would be empty. `stopBank` itself is dropped, as the
// public payload drops it; nothing in this viewer reads it. So are the
// survey recipients (`closing.recipients`).
function sanitizePreview(data) {
  const { stopBank, ...pub } = data || {};
  const starred = (stopBank || []).filter(s => s && s.freeRoam);
  const freeRoamLocations = starred.length
    ? starred.map(s => {
        const { quote, freeRoam, guidance, text, ...rest } = s;
        return { ...rest, description: text || "", icon: s.icon || "📍", primary: !!s.primary };
      })
    : (pub.freeRoamLocations || []);
  const { recipients, ...closing } = pub.closing || {}; // eslint-disable-line no-unused-vars
  return {
    ...pub,
    ...(pub.closing ? { closing } : {}),
    freeRoamLocations,
    tours: (pub.tours || []).map(t => ({
      ...t,
      stops: (t.stops || []).map(s => ({ ...s, hasQuote: !!(s.quote && String(s.quote).trim()) })),
    })),
  };
}
const normalizeQuote = (s) => String(s == null ? "" : s).toUpperCase().replace(/[^A-Z0-9]/g, "");

// Normalize any building tag (eitc_e2, EITC E2, E2, e2) → 'e2'
function normalizeBuilding(b) {
  if (b == null) return null;
  return String(b).toLowerCase().replace(/^eitc[\s_-]*/, '').trim();
}

// Short, human building code used in room labels (E1 / E2 / E3 / SP / SP-C),
// so room numbers read uniformly as "E2-229", "SP-347", etc.
function buildingShortCode(b) {
  const n = normalizeBuilding(b);
  if (!n) return "";
  if (n === "e1") return "E1";
  if (n === "e2") return "E2";
  if (n === "e3") return "E3";
  if (n === "stanley_pauley_engineering") return "SP";
  if (n === "stanley_pauley_centre") return "SP-C";
  return String(b).toUpperCase();
}

// Canonical room-code format: "E2-229", "SP-347".
function formatRoomCode(building, room) {
  const bc = buildingShortCode(building);
  if (!bc) return String(room == null ? "" : room);
  // A hand-placed spot has no room number. Interpolating it printed
  // "E2-null"; the building on its own is the honest answer.
  if (room == null || String(room).trim() === "") return bc;
  return `${bc}-${room}`;
}
// The caption for a stop or bank entry: its room code, or — for a hand-placed
// spot, which has no room — the name it was given.
function stopCode(s) {
  if (!s) return "";
  if (s.room != null && String(s.room).trim() !== "") return formatRoomCode(s.building, s.room);
  return s.label || formatRoomCode(s.building, s.room);
}


// Display helper: render any room-code token with its dash, so freeform titles/
// labels typed without it (e.g. "E2 229") still show consistently as "E2-229".
function withRoomDash(text) {
  if (text == null) return text;
  return String(text).replace(/\b(SP-?C|SPC|E1|E2|E3|SP)\s+(\d+[A-Za-z]?)\b/g, "$1-$2");
}

// UM-style level number — floor 1 → "100", floor 2 → "200", etc.
function levelNumber(f) {
  return Number.isFinite(f) ? `${f * 100}` : "—";
}
function levelLabel(f) {
  return `Level ${levelNumber(f)}`;
}

// Building region labels (E1 / E2 / E3 / Stanley Pauley) fade as the user
// zooms in past the building view. Anchor points:
//   z ≤ 18 → 100%   (full overview — most useful)
//   z = 19 → 50%
//   z = 20 → 20%
//   z ≥ 21 →  0%    (out of the way once you're reading rooms)
function regionLabelOpacityForZoom(z) {
  if (!Number.isFinite(z)) return 0.5;
  if (z <= 18) return 1.0;
  if (z >= 21) return 0;
  if (z <= 19) return 1.0 + (0.5 - 1.0) * (z - 18); // 1.0 → 0.5
  if (z <= 20) return 0.5 + (0.2 - 0.5) * (z - 19); // 0.5 → 0.2
  return       0.2 + (0   - 0.2) * (z - 20);        // 0.2 → 0
}

// Colourblind-friendly building border colours (Okabe–Ito). The building shell
// stays white; a second border-only polygon is drawn on top in this colour so
// each building is outlined distinctly. Keyed by normalized building tag.
const BUILDING_COLORS = {
  e1:                          "#0072B2", // blue
  e2:                          "#E69F00", // orange
  e3:                          "#009E73", // bluish green
  stanley_pauley_centre:       "#CC79A7", // reddish purple
  stanley_pauley_engineering:  "#D55E00", // vermillion
};

// Low-opacity in-region labels (centroids computed from each building's outline
// polygon) so users always see which section of EITC they're looking at.
const BUILDING_LABELS = [
  { key: "e1",                          text: "E1",                  pos: { lat: 49.808309, lng: -97.133585 } },
  { key: "e2",                          text: "E2",                  pos: { lat: 49.808758, lng: -97.133749 } },
  { key: "e3",                          text: "E3",                  pos: { lat: 49.808491, lng: -97.134336 } },
  { key: "stanley_pauley_engineering",  text: "Stanley Pauley\nEng", pos: { lat: 49.807915, lng: -97.134328 } },
  { key: "stanley_pauley_centre",       text: "Stanley Pauley\nCentre", pos: { lat: 49.808018, lng: -97.134005 } },
];

// The Atrium is where Free Roam starts (a tour starts at its own first stop).
// The rule (the known node, else a node labelled "Atrium", else the node nearest
// the EITC centre) is `resolveStartNode` in utils/engtourRoutes.js. This wraps
// it as a routing "entry", the shape every stop resolves to.
function resolveAtrium(graph) {
  const { id, floor } = resolveStartNode(graph?.nodes);
  return { node_ids: [id], room_number: START_LABEL, building: null, floors: new Set([floor]) };
}

// A cheap signature of a tour's hand-drawn routes, folded into the leg builder's
// cache key so a changed drawing rebuilds the legs even if the component is
// reused with the same tour id and mode (the creator's Test tour).
function routesSig(routes) {
  const keys = Object.keys(routes || {}).sort();
  let h = 0;
  for (const k of keys) {
    const str = `${k}=${(routes[k]?.nodeIds || []).join(",")}`;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return `${keys.length}:${(h >>> 0).toString(36)}`;
}

// Does leg i follow the editor's drawing? Pure, so it can be checked without a
// map. null → route automatically (no drawing, or one the graph can no longer
// honour: a node renumbered upstream, or it no longer ends at a door of the
// destination). Otherwise the node ids to walk, and whether the previous leg
// arrived somewhere other than where the drawing starts (`needsConnector`).
function planDrawnLeg(drawn, nodeById, destNodeIds, anchorIds) {
  if (!drawn) return null;
  if (!validateRoute(drawn, nodeById, destNodeIds).ok) return null;
  const ids = drawn.nodeIds;
  return { ids, needsConnector: !(anchorIds || []).includes(ids[0]) };
}

// Free-roam star pins: only curated "primary" landmarks show when zoomed out;
// everything shows past PRIMARY_ZOOM (live-tunable from the debug panel —
// see `DEFAULT_DEBUG` inside the component).

// ── University of Manitoba brand palette ────────────────────────────────────
// Per UM Logo Guidelines (Sept 2022): brown + gold + sky-blue identity.
const UM = {
  brown:   "#4A1E13",   // bison / wordmark — deep warm brown (primary)
  brownDk: "#33150C",
  gold:    "#FFB81C",   // earth / path — UM gold
  goldDk:  "#E0A000",
  sky:     "#0093D0",   // sky / Northern Lights — bright blue
  blue:    "#2C5697",   // mid blue
  navy:    "#1B335E",   // deep blue
  success: "#2E7D32",   // green for completed checkpoints / progress
  cream:   "#FBF6EE",   // warm off-white surface
  sand:    "#F1E7D6",   // subtle panel tint
  line:    "#E4D8C5",   // hairline on cream
  ink:     "#2E2117",   // body text on light
};

// UM serif feel for headings (Ardina is the official wordmark font — not bundled;
// Georgia/serif is the closest safe web fallback). Body stays clean sans.
const SERIF = "'Georgia', 'Times New Roman', serif";
const SANS = "'Helvetica Neue', Arial, sans-serif";

// UM Price Faculty of Engineering logo. Uses the official PNG; falls back to
// a brand-coloured serif wordmark if the asset fails to load.
function UMLogo({ height = 40, reverse = false, onClick }) {
  const [imgOk, setImgOk] = useState(true);
  const clickStyle = onClick
    ? { cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }
    : null;
  if (imgOk) {
    return (
      <img
        // White-text version on dark/brown backgrounds, dark version otherwise
        src={reverse ? "/Map Info/EngTour/UofMPriceFacultyLogoWhiteText.png" : "/Map Info/EngTour/UofMPriceFacultyLogo.png"}
        alt="UM Price Faculty of Engineering"
        style={{ height, width: "auto", display: "block", ...clickStyle }}
        onError={() => setImgOk(false)}
        onClick={onClick}
        draggable={false}
      />
    );
  }
  return (
    <div onClick={onClick} style={{ fontFamily: SERIF, lineHeight: 0.98, color: reverse ? "#fff" : UM.brown, ...clickStyle }}>
      <div style={{ fontSize: height * 0.42, fontWeight: 700, letterSpacing: "0.01em" }}>University</div>
      <div style={{ fontSize: height * 0.52, fontWeight: 800 }}>
        <span style={{ fontSize: height * 0.32, fontStyle: "italic", fontWeight: 600 }}>of </span>Manitoba
      </div>
    </div>
  );
}

// Renders a stop/resource body. Plain text is shown as-is (newlines kept).
// A line in [[ ... ]] is a media block placed between the rows of text:
//   [[Button label|https://url]]  → a tappable link button (URL optional)
//   [[img|path or url]]           → an inline image
//   [[video|youtube or mp4 url]]  → an inline video embed
// (The Tour Creator emits these tokens; legacy stops use images[]/videoUrl.)
function RichText({ text }) {
  if (!text) return null;
  const lines = String(text).split("\n");
  const blocks = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      blocks.push({ type: "text", value: buf.join("\n").replace(/^\n+|\n+$/g, "") });
      buf = [];
    }
  };
  lines.forEach(line => {
    // First field can't contain ] or | ; payload (url/path) may contain spaces.
    const m = line.match(/^\s*\[\[\s*([^\]|]+?)\s*(?:\|\s*([^\]]*?)\s*)?\]\]\s*$/);
    if (m) {
      flush();
      const kind = m[1].toLowerCase();
      if (kind === "img") {
        // payload is "src" or "src|HEIGHT" (px crop height; width stays 100%).
        const parts = String(m[2] || "").split("|");
        let src = m[2] || "", height;
        if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1].trim())) {
          height = Number(parts.pop().trim());
          src = parts.join("|").trim();
        }
        blocks.push({ type: "image", src, height });
      }
      else if (kind === "video") {
        // payload is "url" or "url|autoplay"
        const parts = String(m[2] || "").split("|");
        let autoplay = false;
        if (parts.length > 1 && parts[parts.length - 1].trim().toLowerCase() === "autoplay") { autoplay = true; parts.pop(); }
        blocks.push({ type: "video", url: parts.join("|").trim(), autoplay });
      }
      // safeHref: the tour is authored text, and this goes straight into an
      // href — only http(s), a site path or mailto/tel survive; anything else
      // renders as the same inert button an empty URL does.
      else blocks.push({ type: "button", label: m[1], url: safeHref(m[2]) });
    } else buf.push(line);
  });
  flush();
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === "button") {
          return (
            <a key={i} href={b.url || undefined} target="_blank" rel="noopener noreferrer"
              style={{
                display: "block", textAlign: "center", textDecoration: "none",
                margin: "12px 0", padding: "11px 16px", borderRadius: 11,
                background: UM.gold, color: UM.brown, fontWeight: 800, fontSize: 14,
                cursor: b.url ? "pointer" : "default",
              }}>
              {b.label}
            </a>
          );
        }
        if (b.type === "image") {
          if (!b.src) return null;
          return (
            <img key={i} src={b.src} alt="" style={{ width: "100%", height: b.height ? `${b.height}px` : "auto", maxHeight: b.height ? "none" : 320, objectFit: "cover", borderRadius: 12, display: "block", margin: "12px 0" }}
              onError={e => { e.target.style.display = "none"; }} />
          );
        }
        if (b.type === "video") {
          if (!b.url) return null;
          return (
            <div key={i} style={{ margin: "12px 0", borderRadius: 12, overflow: "hidden", background: "#000" }}>
              <VideoEmbed url={b.url} autoplay={b.autoplay} />
            </div>
          );
        }
        return (
          <div key={i} style={{ fontSize: 14, color: UM.ink, lineHeight: 1.65, whiteSpace: "pre-wrap", margin: i ? "10px 0 0" : 0 }}>
            {b.value}
          </div>
        );
      })}
    </>
  );
}

// "How to read room numbers": one bullet per line, "-" lines nested under the
// one above, **bold** — parsed by parseGuide and rendered as text, never HTML.
// Exported so the creator's "Intro & closing" editor shows the same box.
export function RoomGuideList({ body }) {
  const items = [];
  parseGuide(body).forEach(line => {
    if (line.depth === 1 && items.length) items[items.length - 1].sub.push(line);
    else items.push({ ...line, sub: [] });
  });
  const parts = (ps) => ps.map((p, i) => (p.bold ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>));
  return (
    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: UM.ink, lineHeight: 1.55 }}>
      {items.map((it, i) => (
        <li key={i} style={i ? { marginTop: 4 } : undefined}>
          {parts(it.parts)}
          {it.sub.length > 0 && (
            <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
              {it.sub.map((sub, j) => <li key={j}>{parts(sub.parts)}</li>)}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── Utilities ───────────────────────────────────────────────────────────────

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) { resolve(); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) { existing.addEventListener("load", resolve); return; }
    const script = document.createElement("script");
    script.id = "gmap-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
}

function haversineMeters(n1, n2) {
  const R = 6371000;
  const φ1 = n1.lat * Math.PI / 180, φ2 = n2.lat * Math.PI / 180;
  const Δφ = (n2.lat - n1.lat) * Math.PI / 180;
  const Δλ = (n2.lng - n1.lng) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}


const TYPE_STYLES = {
  outline:        { fillColor: "#ffffff", fillOpacity: 1,    strokeColor: "#aaaaaa", strokeOpacity: 1,   strokeWeight: 2,   zIndex: 0 },
  room:           { fillColor: "#FCF8EF", fillOpacity: 1,    strokeColor: "#dbdbdb", strokeOpacity: 0.9, strokeWeight: 1.5, zIndex: 1 },
  elevatorstairs: { fillColor: "#9e9e9e", fillOpacity: 1,    strokeColor: "#616161", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  stair:          { fillColor: "#c4956a", fillOpacity: 1,    strokeColor: "#a0724a", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  elevator:       { fillColor: "#b0bec5", fillOpacity: 1,    strokeColor: "#78909c", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  corridor:       { fillColor: "#f5f5f5", fillOpacity: 1,    strokeColor: "#cccccc", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
  washroom:       { fillColor: "#e3f2fd", fillOpacity: 1,    strokeColor: "#90caf9", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  other:          { fillColor: "#eeeeee", fillOpacity: 0.85, strokeColor: "#bdbdbd", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
};
const getStyle = (t) => TYPE_STYLES[t] || TYPE_STYLES.other;

function svgUrl(svg) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Small icon badges marking stairs / elevators so users can plan their
// own way between floors when elevators get crowded. Icons (not words) keep
// the map uncluttered: a step glyph for stairs, up/down arrows for elevators.
function stairsLabelIcon() {
  const s = 24;
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}"><rect x="0.5" y="0.5" rx="6" ry="6" width="${s - 1}" height="${s - 1}" fill="#5d4037" stroke="#fff" stroke-width="1.5" opacity="0.92"/><path d="M5 18 h4 v-4 h4 v-4 h4 v-4 h2" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`),
    scaledSize: new window.google.maps.Size(s, s),
    anchor: new window.google.maps.Point(s / 2, s / 2),
  };
}

function elevatorLabelIcon() {
  const s = 24;
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}"><rect x="0.5" y="0.5" rx="6" ry="6" width="${s - 1}" height="${s - 1}" fill="#37474f" stroke="#fff" stroke-width="1.5" opacity="0.92"/><polygon points="8.5,10 12,5.5 15.5,10" fill="#fff"/><polygon points="8.5,14 15.5,14 12,18.5" fill="#fff"/></svg>`),
    scaledSize: new window.google.maps.Size(s, s),
    anchor: new window.google.maps.Point(s / 2, s / 2),
  };
}

// Big translucent in-region label (E1 / E2 / E3 / Stanley Pauley) so users
// always know which section they're in. Two-line strings supported via "\n".
// `strokeColor` matches the building's outline colour so the label reads as
// part of the same colour-coding system.
function regionLabelIcon(text, opacity = 0.42, strokeColor = "#1B335E") {
  const lines = String(text).split("\n");
  const longest = Math.max(...lines.map(l => l.length));
  const fontSize = 22;
  const lineHeight = 26;
  const w = Math.max(56, longest * (fontSize * 0.6) + 16);
  const h = lines.length * lineHeight + 12;
  const textEls = lines.map((line, i) =>
    `<text x="${w / 2}" y="${lineHeight + i * lineHeight}" font-size="${fontSize}" font-family="Georgia,Times,serif" font-weight="900" fill="#ffffff" stroke="${strokeColor}" stroke-width="4" paint-order="stroke" text-anchor="middle">${line}</text>`
  ).join("");
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" opacity="${opacity}">${textEls}</svg>`),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h / 2),
  };
}

// Pull the 11-char video id out of any common YouTube URL form
// (watch?v=, youtu.be/, embed/, shorts/, live/). Returns null if not YouTube.
function youtubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/
  );
  return m ? m[1] : null;
}

// Renders a YouTube URL as an embedded iframe (a <video> tag can't play
// YouTube), and any other URL as a direct HTML5 <video>.
// `autoplay` starts the video the moment the stop popup renders. Browsers only
// permit autoplay when muted, so an autoplaying video starts muted.
function VideoEmbed({ url, autoplay = false }) {
  const yt = youtubeId(url);
  if (yt) {
    const params = autoplay ? "?autoplay=1&mute=1&playsinline=1" : "";
    return (
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <iframe
          src={`https://www.youtube.com/embed/${yt}${params}`}
          title="Video"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return <video src={url} controls autoPlay={autoplay} muted={autoplay} playsInline={autoplay} style={{ width: "100%", display: "block" }} />;
}

// Free-roam place of interest — a star pin instead of a name bubble, so the
// map stays uncluttered. The name lives in the list + the tap-to-open popup.
function freeRoamStarIcon(primary) {
  const s = primary ? 30 : 24;
  // 5-point star on a 24×24 grid, scaled to `s`
  const pts = "12,2.5 15,9 22,9.6 16.8,14.3 18.4,21.3 12,17.6 5.6,21.3 7.2,14.3 2,9.6 9,9";
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24"><polygon points="${pts}" fill="${UM.gold}" stroke="${UM.navy}" stroke-width="1.6" stroke-linejoin="round"/></svg>`),
    scaledSize: new window.google.maps.Size(s, s),
    anchor: new window.google.maps.Point(s / 2, s / 2),
  };
}

function stopMarkerIcon(num, active, done, startHere = false) {
  // done = UM gold (collected), active = UM sky-blue (current target), pending = UM brown
  // Completed checkpoints read as green (progress); current = sky; pending = navy.
  const color = done ? UM.success : active ? UM.sky : UM.navy;
  const txtFill = "#ffffff";
  if (startHere && !done) {
    // The first stop is where the visitor begins: a pill saying so, in place
    // of a number, until it is stamped and becomes a tick like the others.
    return {
      url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="124" height="58">
        <rect x="3" y="5" width="118" height="36" rx="18" fill="${color}" stroke="white" stroke-width="3"/>
        <text x="62" y="29" font-size="16" font-family="Arial,sans-serif" font-weight="bold" fill="${txtFill}" text-anchor="middle">Start Here!</text>
        <polygon points="55,42 69,42 62,56" fill="${color}"/>
      </svg>`),
      scaledSize: new window.google.maps.Size(124, 58),
      anchor: new window.google.maps.Point(62, 56),
    };
  }
  return {
    url: svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="46" height="58">
      <circle cx="23" cy="23" r="20" fill="${color}" stroke="white" stroke-width="3"/>
      <text x="23" y="29" font-size="18" font-family="Arial,sans-serif" font-weight="bold" fill="${txtFill}" text-anchor="middle">${done ? "✓" : num}</text>
      <polygon points="16,42 30,42 23,56" fill="${color}"/>
    </svg>`),
    scaledSize: new window.google.maps.Size(46, 58),
    anchor: new window.google.maps.Point(23, 56),
  };
}


// ── Component ───────────────────────────────────────────────────────────────

export default function EngTourPage({ previewData = null, previewTourId = null, previewFromStart = false } = {}) {
  // Preview mode: skip auth and the fetch and drive the draft data instead of
  // the API. Sanitized once (stable identity) so activeTour stays the same
  // object as tourData.tours[i]. Two ways in:
  //  - default: jump straight into the tour named by `previewTourId` (the
  //    Creator's "Test tour" today, and the shape a "jump to this stop"
  //    shortcut would use);
  //  - `previewFromStart`: begin exactly where a visitor does — the welcome
  //    screen, the tour picker, the room-number primer, the tour, the
  //    completion screen — so the initial state below mirrors the public
  //    page's and `previewTourId` is deliberately ignored: the picker is the
  //    thing being tested.
  const previewRef = useRef(null);
  if (previewData && !previewRef.current) previewRef.current = sanitizePreview(previewData);
  const previewTourData = previewData ? previewRef.current : null;
  const isPreview = !!previewData;
  const previewJumpsIn = isPreview && !previewFromStart;

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const navGraphRef = useRef(null);
  const buildingPolysRef = useRef([]);
  const routeSegmentsRef = useRef([]); // { polyline, floor, legIdx }  — legIdx: -1 for freeroam single-route, 0..n-1 for tour legs
  const arrowAnimRef = useRef(null);   // interval id for the moving direction arrows
  const routeTransitionsRef = useRef([]); // { overlay, fromFloor, legIdx } floor-change boxes
  const stopMarkersRef = useRef([]);
  const destPolyHighlightRef = useRef([]);
  const tourLegsBuiltForTourRef = useRef(null); // id of tour whose legs are currently drawn
  const stopDoorNodeRef = useRef({}); // stopIndex → the exact door node id the route enters/exits

  // ── Tour analytics (for the completion email) ──────────────────────────────
  const surveyRef = useRef(null);          // { prompt, optionId, optionLabel }
  const stampMethodRef = useRef({});       // stopIdx -> 'quote' | 'skip'
  const tourStartRef = useRef(null);       // epoch ms when the tour began
  const tourEndRef = useRef(null);         // epoch ms when the tour completed
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | sending | sent | error
  const FloorTransitionOverlayRef = useRef(null); // OverlayView subclass (created after maps load)
  const FreeRoamStarOverlayRef = useRef(null); // pulsing star OverlayView (created after maps load)
  const freeRoamOverlaysRef = useRef([]);         // { overlay: Marker, loc } — star pins

  // Auth — token issued by /api/auth gates the protected tour endpoints.
  // Preview bypasses the gate entirely (the creator is already authenticated).
  const tokenRef = useRef(sessionStorage.getItem("engtour_token") || null);
  const [authed, setAuthed] = useState(() => isPreview || !!sessionStorage.getItem("engtour_token"));
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwRateLimited, setPwRateLimited] = useState(false);

  // Tour state
  const [tourData, setTourData] = useState(previewTourData); // { questions, tours, freeRoamLocations }
  const [stage, setStage] = useState(previewJumpsIn ? "touring" : "questionnaire"); // 'questionnaire' | 'touring' | 'complete' | 'freeroam'
  // Intro flow within the questionnaire stage: welcome → pick a tour → how to
  // read room numbers → start.
  const [introStep, setIntroStep] = useState("intro"); // 'intro' | 'pick' | 'howto'
  const [pendingTourId, setPendingTourId] = useState(null);
  const [activeTour, setActiveTour] = useState(() =>
    previewJumpsIn ? (previewTourData.tours || []).find(t => t.id === previewTourId) || (previewTourData.tours || [])[0] || null : null
  ); // { id, title, stops }
  const [currentStopIdx, setCurrentStopIdx] = useState(0);
  // Mirror for the leg builder, which must not re-run when the stop changes.
  const currentStopIdxRef = useRef(0);
  useEffect(() => { currentStopIdxRef.current = currentStopIdx; }, [currentStopIdx]);
  const [stampedStops, setStampedStops] = useState([]); // array of stop indices
  const [quoteInput, setQuoteInput] = useState("");
  const [quoteWrong, setQuoteWrong] = useState(false);
  const [quoteChecking, setQuoteChecking] = useState(false);
  const [stopPopupOpen, setStopPopupOpen] = useState(false); // shows after stamping
  const [resourcesOpen, setResourcesOpen] = useState(false);
  // The completion form's answers, keyed by field id. Which fields exist, what
  // they are called and which are required is the tour's (closing.fields,
  // src/utils/engtourIntro.js), set in the creator's "Intro & closing".
  const [contactValues, setContactValues] = useState({});
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [freeRoamSelection, setFreeRoamSelection] = useState(null); // a freeRoam location (info popup)
  const [freeRoamListOpen, setFreeRoamListOpen] = useState(true);
  const [freeRoamFilter, setFreeRoamFilter] = useState(null); // null = All; else a type/tag
  const [freeRoamSearch, setFreeRoamSearch] = useState("");
  // Where free-roam routes start from: null = the Atrium, else the last place
  // the user routed to (they "moved" there).
  const [freeRoamOrigin, setFreeRoamOrigin] = useState(null);
  // Flips true once the nav graph has loaded. Kept in state (not just a ref) so
  // route/marker builders re-run when it arrives — critical for the Tour Creator
  // preview, which (without `previewFromStart`) mounts straight into "touring"
  // before the graph is ready.
  const [navReady, setNavReady] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(2);
  const [mapReady, setMapReady] = useState(false);
  const [accessibleMode, setAccessibleMode] = useState(() => sessionStorage.getItem("engtour_a11y") === "1");
  useEffect(() => { sessionStorage.setItem("engtour_a11y", accessibleMode ? "1" : "0"); }, [accessibleMode]);
  const [mapZoom, setMapZoom] = useState(19);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  // keep latest zoom in a ref so overlay class can read it without re-creating
  const mapZoomRef = useRef(19);

  // ── Debug mode (tap the logo 10× to unlock) ──────────────────────────────
  // Tunable thresholds so I can dial things in live and tell Claude what works.
  const DEFAULT_DEBUG = {
    primaryZoom:        20,    // map zoom below which only "primary" pins show
    stairElevatorZoom:  19,    // stair/elevator icons appear at zoom ≥ this (hidden at 18)
  };
  const [debugMode, setDebugMode] = useState(false);
  const [debugSettings, setDebugSettings] = useState(DEFAULT_DEBUG);
  const debugRef = useRef(DEFAULT_DEBUG);
  useEffect(() => { debugRef.current = debugSettings; }, [debugSettings]);
  const regionLabelMarkersRef = useRef([]);

  // Region label opacity is driven by the current zoom (fully visible at z≤18,
  // fully transparent at z≥21). Markers stay on the map; they just fade.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    // Free roam keeps the zoom-driven fade; every other stage (i.e. an active
    // tour) pins the building labels at full opacity so they're always legible.
    const opacity = stage === "freeroam" ? regionLabelOpacityForZoom(mapZoom) : 1;
    const visible = opacity > 0.001;
    regionLabelMarkersRef.current.forEach(({ marker, text, stroke }) => {
      if (visible) marker.setIcon(regionLabelIcon(text, opacity, stroke));
      marker.setMap(visible ? map : null);
    });
  }, [mapZoom, stage]);
  const logoTapsRef = useRef({ count: 0, timer: null });
  const handleLogoTap = useCallback(() => {
    const r = logoTapsRef.current;
    if (r.timer) clearTimeout(r.timer);
    r.count += 1;
    if (r.count >= 10) {
      r.count = 0; r.timer = null;
      setDebugMode(d => !d);
      return;
    }
    r.timer = setTimeout(() => { r.count = 0; r.timer = null; }, 1500);
  }, []);
  useEffect(() => { mapZoomRef.current = mapZoom; }, [mapZoom]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Stop the arrow animation interval if the page unmounts
  useEffect(() => () => { if (arrowAnimRef.current) clearInterval(arrowAnimRef.current); }, []);

  // ── Password ─────────────────────────────────────────────────────────────
  const handlePasswordSubmit = useCallback(async () => {
    if (pwLoading || pwRateLimited || !pwInput.trim()) return;
    setPwLoading(true); setPwError(false);
    try {
      const res = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwInput }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.token) { tokenRef.current = data.token; sessionStorage.setItem("engtour_token", data.token); }
        setAuthed(true); setPwInput('');
      } else if (res.status === 429) setPwRateLimited(true);
      else setPwError(true);
    } catch { setPwError(true); }
    finally { setPwLoading(false); }
  }, [pwInput, pwLoading, pwRateLimited]);

  // ── Load tour data (quotes stripped server-side; requires token) ──────────
  useEffect(() => {
    if (!authed || isPreview) return; // preview uses the draft data, not the API
    fetch(`${API_BASE}/api/tour-data`, {
      headers: tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {},
    })
      .then(r => {
        if (r.status === 401) {
          // Token missing/expired — drop back to the password gate
          sessionStorage.removeItem("engtour_token"); tokenRef.current = null;
          setAuthed(false);
          throw new Error("unauthorized");
        }
        return r.json();
      })
      .then(setTourData)
      .catch(e => { if (e.message !== "unauthorized") console.error("Failed to load tour data", e); });
  }, [authed, isPreview]);

  // ── Init map ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    async function init() {
      await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
      if (cancelled) return;
      const map = new window.google.maps.Map(mapRef.current, {
        center: EITC_CENTER, zoom: 19, mapTypeId: "satellite", tilt: 0,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      mapInstanceRef.current = map;

      map.addListener("zoom_changed", () => setMapZoom(map.getZoom()));

      // ── Floor-transition box (tap to switch floors) — like /eitc & /stuclark ──
      class FloorTransitionOverlay extends window.google.maps.OverlayView {
        constructor(position, fromFloor, toFloor, transType, onClick) {
          super();
          this.position = position; this.fromFloor = fromFloor;
          this.toFloor = toFloor; this.transType = transType;
          this.onClick = onClick; this._div = null;
        }
        onAdd() {
          const div = document.createElement("div");
          const isUp = this.toFloor > this.fromFloor;
          div.style.cssText = [
            "position:absolute", `background:${UM.cream}`, "border-radius:10px",
            "padding:6px 12px", `font-family:${SANS}`,
            "box-shadow:0 2px 10px rgba(0,0,0,0.35)", "transform:translate(-50%,-50%)",
            "cursor:pointer", "text-align:center", `border:2px solid ${UM.gold}`,
            "pointer-events:auto", "user-select:none", "white-space:nowrap",
          ].join(";");
          div.innerHTML =
            `<div style="font-size:12px;font-weight:800;color:${UM.brown}">${this.transType} ${isUp ? "▲" : "▼"}</div>` +
            `<div style="font-size:11px;font-weight:700;color:${UM.sky}">${this.fromFloor * 100} → ${this.toFloor * 100}</div>` +
            `<div style="font-size:9px;color:#8a7a66;margin-top:1px">tap to switch</div>`;
          div.addEventListener("click", (e) => { e.stopPropagation(); this.onClick(this.toFloor); });
          this._div = div;
          this.getPanes().overlayMouseTarget.appendChild(div);
        }
        draw() {
          const p = this.getProjection()?.fromLatLngToDivPixel(this.position);
          if (p && this._div) { this._div.style.left = p.x + "px"; this._div.style.top = p.y + "px"; }
        }
        onRemove() { if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; } }
      }
      FloorTransitionOverlayRef.current = FloorTransitionOverlay;

      // ── Free-roam place marker: a modern gold star that gently pulses ──
      class FreeRoamStarOverlay extends window.google.maps.OverlayView {
        constructor(position, primary, onClick) {
          super();
          this.position = position; this.primary = primary; this.onClick = onClick; this._div = null;
        }
        onAdd() {
          const size = this.primary ? 42 : 32;
          const div = document.createElement("div");
          div.style.cssText = `position:absolute;transform:translate(-50%,-50%);width:${size}px;height:${size}px;cursor:pointer;pointer-events:auto`;
          div.innerHTML =
            `<svg width="${size}" height="${size}" viewBox="0 0 40 40" style="display:block;overflow:visible;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.55))">` +
            `<defs><radialGradient id="frstar" cx="50%" cy="38%" r="64%"><stop offset="0%" stop-color="#FFC53D"/><stop offset="55%" stop-color="#E8850C"/><stop offset="100%" stop-color="#A84B00"/></radialGradient></defs>` +
            `<polygon points="20,3.5 23.88,14.66 35.69,14.9 26.28,22.04 29.7,33.35 20,26.6 10.3,33.35 13.72,22.04 4.31,14.9 16.12,14.66" ` +
            `fill="url(#frstar)" stroke="#3D1A0A" stroke-width="1.7" stroke-linejoin="round"/></svg>`;
          const star = div.firstChild;
          star.style.transformOrigin = "center";
          // Pulse via the Web Animations API — smooth, no CSS keyframes needed.
          try {
            star.animate(
              [{ transform: "scale(0.82)" }, { transform: "scale(1.14)" }, { transform: "scale(0.82)" }],
              { duration: 1500, iterations: Infinity, easing: "ease-in-out" }
            );
          } catch { /* animation unsupported — static star is fine */ }
          div.addEventListener("click", (e) => { e.stopPropagation(); this.onClick(); });
          this._div = div;
          this.getPanes().overlayMouseTarget.appendChild(div);
        }
        draw() {
          const p = this.getProjection()?.fromLatLngToDivPixel(this.position);
          if (p && this._div) { this._div.style.left = p.x + "px"; this._div.style.top = p.y + "px"; }
        }
        onRemove() { if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; } }
      }
      FreeRoamStarOverlayRef.current = FreeRoamStarOverlay;

      try {
        const results = await Promise.all(
          BUILDING_KEYS.map(({ key, building }) =>
            fetch(`${API_BASE}/api/building?key=${encodeURIComponent(key)}`).then(r => r.json()).then(data => ({ data, building }))
          )
        );
        results.forEach(({ data, building }) => {
          const bColor = BUILDING_COLORS[normalizeBuilding(building)];
          (data.polygons || []).forEach(item => {
            if (!item.coordinates?.length) return;
            const type = item.type?.toLowerCase() || "other";
            const style = { ...getStyle(type) };
            const floor = item.floor ?? 1;
            const paths = item.coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }));
            // Base shell stays white (default outline style) behind the rooms.
            const poly = new window.google.maps.Polygon({
              paths, ...style, clickable: false,
              map: floor === 2 ? map : null,
            });
            buildingPolysRef.current.push({
              poly, floor, building,
              room_number: item.room_number ? String(item.room_number) : null,
              origFill: style.fillColor, origOpacity: style.fillOpacity, origStroke: style.strokeColor,
            });

            // Second, border-only outline drawn ABOVE every other polygon, in the
            // building's colourblind-friendly colour — so each building reads as a
            // distinct colour while its shell stays a clean white fill underneath.
            if (type === "outline" && bColor) {
              const colorPoly = new window.google.maps.Polygon({
                paths, clickable: false,
                strokeColor: bColor, strokeOpacity: 0.6, strokeWeight: 4,
                fillOpacity: 0, zIndex: 50,
                map: floor === 2 ? map : null,
              });
              buildingPolysRef.current.push({
                poly: colorPoly, floor, building, room_number: null,
                origFill: null, origOpacity: 0, origStroke: bColor,
              });
            }


            // Label stairs and elevators so students can spot alternate
            // floor-change options at a glance (handy when elevators are busy).
            // Tagged `stairElevator` so the zoom-aware visibility effect can
            // hide them when the map is too zoomed out.
            if (type === "stair" || type === "elevator") {
              const lat = paths.reduce((s, p) => s + p.lat, 0) / paths.length;
              const lng = paths.reduce((s, p) => s + p.lng, 0) / paths.length;
              const marker = new window.google.maps.Marker({
                position: { lat, lng },
                icon: type === "stair" ? stairsLabelIcon() : elevatorLabelIcon(),
                map: null, // visibility is set by the floor + zoom effect below
                clickable: false,
                zIndex: 80,
              });
              buildingPolysRef.current.push({
                poly: marker, floor, building, room_number: null,
                kind: "stairElevator",
                origFill: null, origOpacity: 0, origStroke: null,
              });
            }
          });
        });
      } catch (e) { console.error("Failed to load building polygons", e); }

      // In-region building labels (E1 / E2 / E3 / Stanley Pauley), outlined in
      // each building's own colour so they read as part of the same colour
      // system. Kept in a ref so the zoom-curve effect can re-tune opacity live.
      BUILDING_LABELS.forEach(b => {
        const stroke = BUILDING_COLORS[b.key] || "#1B335E";
        const marker = new window.google.maps.Marker({
          position: b.pos,
          icon: regionLabelIcon(b.text, regionLabelOpacityForZoom(mapZoomRef.current), stroke),
          map,
          clickable: false,
          zIndex: 60,           // above the building border (50), below pins (600)
          optimized: false,
        });
        regionLabelMarkersRef.current.push({ marker, text: b.text, stroke });
      });

      setMapReady(true);
    }
    init();
    return () => { cancelled = true; };
  }, [authed]);

  // ── Load nav graph ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    fetch(`${API_BASE}/api/navgraph?keys=${EITC_NAV_KEYS.join(',')}`)
      .then(r => r.json())
      .then(data => { navGraphRef.current = { nodes: data.nodes || [], rooms: data.rooms || [] }; setNavReady(true); })
      .catch(e => console.error("Failed to load nav graphs", e));
  }, [mapReady]);

  // ── Style a single polyline based on (legIdx, floor) vs current state ───
  // Only the leg being walked is on the map. Every leg is still BUILT up front
  // (the pins sit on the doors the legs actually use), but the ones already
  // walked and the ones still to come are hidden rather than drawn faint:
  // with ten legs through four floors the previews read as a tangle, and the
  // numbered pins already say where the tour goes next.
  const styleSegment = useCallback((seg, currentLeg, floor) => {
    const active = seg.legIdx === -1 || seg.legIdx === currentLeg;
    if (!active) { seg.polyline.setOptions({ visible: false }); return; }
    const onFloor = seg.floor === floor || isOutdoorFloor(seg.floor);
    seg.polyline.setOptions({
      visible: true, strokeColor: UM.gold,
      strokeOpacity: onFloor ? 0.98 : 0.4, strokeWeight: onFloor ? 7 : 3, zIndex: onFloor ? 500 : 400,
    });
  }, []);

  // Moving direction arrows that ride along the active (gold) leg on the
  // current floor — a subtle cue for which way to walk.
  const animateActiveArrows = useCallback(() => {
    if (arrowAnimRef.current) { clearInterval(arrowAnimRef.current); arrowAnimRef.current = null; }
    const arrowIcon = {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3, fillColor: UM.navy, fillOpacity: 1, strokeColor: UM.navy, strokeWeight: 1,
    };
    // offset/repeat in PIXELS (not %) so the arrows move at a constant
    // on-screen speed no matter how long or short the path is.
    const REPEAT = 110;     // px between arrows
    const STEP = 2;         // px advanced per tick → fixed velocity
    const active = [];
    routeSegmentsRef.current.forEach(seg => {
      const onFloor = seg.floor === selectedFloor || isOutdoorFloor(seg.floor);
      const isActive = seg.legIdx === -1 || seg.legIdx === currentStopIdx;
      if (isActive && onFloor) {
        seg.polyline.setOptions({ icons: [{ icon: arrowIcon, offset: "0px", repeat: REPEAT + "px" }] });
        active.push(seg.polyline);
      } else {
        seg.polyline.setOptions({ icons: [] });
      }
    });
    if (active.length === 0) return;
    let px = 0;
    arrowAnimRef.current = setInterval(() => {
      px = (px + STEP) % REPEAT;
      const o = px + "px";
      active.forEach(pl => {
        const ic = pl.get("icons");
        if (ic && ic[0]) { ic[0].offset = o; pl.set("icons", ic); }
      });
    }, 60);
  }, [currentStopIdx, selectedFloor]);

  const restyleAllSegments = useCallback(() => {
    routeSegmentsRef.current.forEach(seg => styleSegment(seg, currentStopIdx, selectedFloor));
    animateActiveArrows();
  }, [styleSegment, currentStopIdx, selectedFloor, animateActiveArrows]);

  // Show transition boxes only for the active leg & current floor
  const restyleTransitions = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    routeTransitionsRef.current.forEach(({ overlay, fromFloor, legIdx }) => {
      const onActiveLeg = legIdx === -1 || legIdx === currentStopIdx;
      overlay.setMap(onActiveLeg && fromFloor === selectedFloor ? map : null);
    });
  }, [currentStopIdx, selectedFloor]);

  // ── Floor / zoom visibility ──────────────────────────────────────────────
  // Stair / elevator labels are floor-filtered AND zoom-gated (they declutter
  // out at low zoom, where the building outline is the main signal).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const stairZoomOK = mapZoom >= debugSettings.stairElevatorZoom;
    buildingPolysRef.current.forEach(({ poly, floor, kind }) => {
      const floorOK = floor === selectedFloor;
      const zoomOK = kind === "stairElevator" ? stairZoomOK : true;
      poly.setMap(floorOK && zoomOK ? map : null);
    });
    restyleAllSegments();
    restyleTransitions();
  }, [selectedFloor, mapZoom, debugSettings.stairElevatorZoom, restyleAllSegments, restyleTransitions]);

  // ── Find a node id for (building, room) — falls back if primary missing ─
  const findRoomEntry = useCallback((building, roomNumber, fallbackBuilding, fallbackRoom) => {
    const graph = navGraphRef.current;
    if (!graph) return null;
    const tryLookup = (b, r) => {
      if (r == null) return null;
      const bn = normalizeBuilding(b);
      const matches = graph.nodes.filter(n =>
        (bn == null || normalizeBuilding(n.building) === bn) &&
        String(n.room_number) === String(r)
      );
      if (matches.length === 0) return null;
      return {
        building: bn,
        room_number: String(r),
        node_ids: matches.map(n => n.id),
        floors: new Set(matches.map(n => n.floor).filter(Number.isFinite)),
      };
    };
    return tryLookup(building, roomNumber) || tryLookup(fallbackBuilding || building, fallbackRoom);
  }, []);

  // ── Resolve a stop to nav nodes ──────────────────────────────────────────
  // Most stops are rooms and are found by their code. A stop created with the
  // creator's "Create Spot" is a place with no room — the middle of the atrium,
  // say — and names the nav node it was pinned to instead. Anything saved
  // before spots existed carries no `nodeId` and takes exactly the path it
  // always did; if a pinned node ever disappears from the graph, the room
  // lookup still runs as a fallback rather than the stop routing nowhere.
  const entryFor = useCallback((s) => {
    if (!s) return null;
    if (s.nodeId) {
      const node = navGraphRef.current?.nodes.find(n => n.id === s.nodeId);
      if (node) {
        return {
          building: normalizeBuilding(node.building),
          room_number: node.room_number != null ? String(node.room_number) : (s.label || ""),
          node_ids: [node.id],
          floors: new Set(Number.isFinite(node.floor) ? [node.floor] : []),
        };
      }
    }
    return findRoomEntry(s.building, s.room, s.fallbackBuilding, s.fallbackRoom);
  }, [findRoomEntry]);

  // ── Highlight destination polygon ────────────────────────────────────────
  const highlightDestRoom = useCallback((roomNumber, building) => {
    destPolyHighlightRef.current.forEach(({ poly, origFill, origOpacity, origStroke }) => {
      poly.setOptions({ fillColor: origFill, fillOpacity: origOpacity, strokeColor: origStroke, strokeWeight: 1.5 });
    });
    destPolyHighlightRef.current = [];
    if (!roomNumber) return;
    const bn = normalizeBuilding(building);
    const matches = buildingPolysRef.current.filter(p =>
      p.room_number === String(roomNumber) && (!bn || normalizeBuilding(p.building) === bn)
    );
    matches.forEach(item => {
      item.poly.setOptions({ fillColor: "#ffd54f", fillOpacity: 0.78, strokeColor: "#f57f17", strokeWeight: 3 });
      destPolyHighlightRef.current.push(item);
    });
  }, []);

  // ── Clear route ──────────────────────────────────────────────────────────
  const clearRoute = useCallback(() => {
    if (arrowAnimRef.current) { clearInterval(arrowAnimRef.current); arrowAnimRef.current = null; }
    routeSegmentsRef.current.forEach(({ polyline }) => polyline.setMap(null));
    routeSegmentsRef.current = [];
    routeTransitionsRef.current.forEach(({ overlay }) => overlay.setMap(null));
    routeTransitionsRef.current = [];
  }, []);

  const clearStopMarkers = useCallback(() => {
    stopMarkersRef.current.forEach(m => m.setMap(null));
    stopMarkersRef.current = [];
  }, []);

  // ── Find best path (node list) for a single leg ─────────────────────────
  const findBestPath = useCallback(async (startEntry, destEntry, accessibleOnly = false) => {
    if (!startEntry || !destEntry) return null;
    let bestPath = null, bestDist = Infinity, bestNodes = null;
    for (const startId of startEntry.node_ids) {
      for (const endId of destEntry.node_ids) {
        if (startId === endId) continue;
        try {
          const candidateNodes = await fetch(`${API_BASE}/api/pathfind`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            // Default routes take the stairs; only accessible mode allows elevators.
            body: JSON.stringify({ navKeys: EITC_NAV_KEYS, startId, endId, accessibleOnly, avoidElevators: !accessibleOnly }),
          }).then(r => r.json());
          if (!Array.isArray(candidateNodes) || candidateNodes.length < 2) continue;
          let d = 0;
          for (let i = 0; i < candidateNodes.length - 1; i++) d += haversineMeters(candidateNodes[i], candidateNodes[i + 1]);
          if (d < bestDist) {
            bestDist = d;
            bestPath = candidateNodes.map(n => n.id);
            bestNodes = candidateNodes;
          }
        } catch { /* skip */ }
      }
    }
    return bestPath && bestNodes ? { ids: bestPath, nodes: bestNodes } : null;
  }, []);

  // ── Draw one leg's polylines (tagged with legIdx) ───────────────────────
  const drawLegSegments = useCallback((nodes, legIdx) => {
    const map = mapInstanceRef.current;
    if (!map || !nodes?.length) return;
    const FTO = FloorTransitionOverlayRef.current;
    // One polyline per floor; a stair or elevator run is ONE floor change
    // (`splitLegByFloor`: 100 → 300 is one tap, not two).
    const { segments, transitions } = splitLegByFloor(nodes);
    if (FTO) {
      transitions.forEach(({ lat, lng, fromFloor, toFloor, type }) => {
        const transType = type === "elevator" ? "Elevator" : "Stairs";
        const pos = new window.google.maps.LatLng(lat, lng);
        // A box on each side, so the switch can be undone from the other floor.
        [[fromFloor, toFloor], [toFloor, fromFloor]].forEach(([fF, tF]) => {
          const overlay = new FTO(pos, fF, tF, transType, (f) => setSelectedFloor(f));
          routeTransitionsRef.current.push({ overlay, fromFloor: fF, legIdx });
        });
      });
    }
    segments.forEach(({ floor, coords }) => {
      const polyline = new window.google.maps.Polyline({ path: coords, map });
      const entry = { polyline, floor, legIdx };
      styleSegment(entry, currentStopIdx, selectedFloor);
      routeSegmentsRef.current.push(entry);
    });
  }, [styleSegment, currentStopIdx, selectedFloor]);

  // ── Free-roam: single-route render (uses legIdx = -1) ───────────────────
  const runRoute = useCallback(async (startEntry, destEntry) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    clearRoute();
    const path = await findBestPath(startEntry, destEntry, accessibleMode);
    if (!path) return;
    drawLegSegments(path.nodes, -1);
    const bounds = new window.google.maps.LatLngBounds();
    path.nodes.forEach(n => bounds.extend({ lat: n.lat, lng: n.lng }));
    map.fitBounds(bounds, 100);
    const startFloor = path.nodes[0]?.floor ?? 2;
    setSelectedFloor(startFloor);
    // Show the floor tap-to-switch boxes for this free-roam route too
    restyleAllSegments();
    restyleTransitions();
  }, [clearRoute, findBestPath, drawLegSegments, accessibleMode, restyleAllSegments, restyleTransitions]);

  // ── Place tour stop markers ──────────────────────────────────────────────
  const renderTourMarkers = useCallback(() => {
    clearStopMarkers();
    const map = mapInstanceRef.current;
    if (!map || !activeTour) return;
    const graph = navGraphRef.current;
    if (!graph) return;
    activeTour.stops.forEach((stop, i) => {
      const entry = entryFor(stop);
      if (!entry) return;
      // Place the pin at the EXACT door the route enters/exits (locked during
      // leg building), so multi-door rooms don't show the pin on a door the
      // path never uses. Fall back to the room's first door before legs build.
      const doorId = stopDoorNodeRef.current[i];
      const n = (doorId && graph.nodes.find(nn => nn.id === doorId))
             || graph.nodes.find(nn => entry.node_ids.includes(nn.id));
      if (!n) return;
      const done = stampedStops.includes(i);
      const active = i === currentStopIdx && !done;
      const marker = new window.google.maps.Marker({
        position: { lat: n.lat, lng: n.lng }, map,
        icon: stopMarkerIcon(i + 1, active, done, i === 0),
        zIndex: active ? 700 : 600,
        title: withRoomDash(stop.title || stop.label),
      });
      stopMarkersRef.current.push(marker);
    });
  }, [activeTour, currentStopIdx, stampedStops, clearStopMarkers, entryFor]);

  // Latest renderTourMarkers in a ref so the leg builder can refresh pins once
  // the actual entry doors are known, without re-running the build on every change.
  const renderMarkersRef = useRef(() => {});
  useEffect(() => { renderMarkersRef.current = renderTourMarkers; }, [renderTourMarkers]);

  // Latest restyle fns kept in a ref so the stop-change effect doesn't re-run
  // (and fight a manual floor switch) just because selectedFloor changed, and so
  // the leg builder can kick off the arrow animation once segments exist.
  const restyleRef = useRef(() => {});
  useEffect(() => {
    restyleRef.current = () => { restyleAllSegments(); restyleTransitions(); };
  }, [restyleAllSegments, restyleTransitions]);

  // ── Build all tour legs once per tour ───────────────────────────────────
  // A leg is either the editor's hand-drawn route (tour.routes, from the
  // creator's Route Adjuster) or the automatic /api/pathfind answer. Drawings
  // are keyed by the leg's TWO ENDS and the mode (`start|e3::270|std`), not by
  // position: reordering or inserting a stop then invalidates exactly the legs
  // whose ends changed, and every other drawing stays with the pair it was
  // drawn for. A drawing is walked only while the graph can still honour it
  // (every node exists, it ends at a door of this stop); otherwise the leg is
  // routed automatically, never drawn as a partial line.
  //
  // The previous leg may arrive at a different door than a drawing starts
  // from — the leg before it was re-routed automatically, or a door was
  // renumbered upstream. That mismatch is AUTO-CONNECTED (a short routed hop
  // from where we are to the drawing's first node), never discarded: an
  // upstream change to one leg must not silently undo an editor's work on the
  // next. If no connector can be found the drawing is still walked on its own.
  //
  // The tour starts AT its first stop — the pin reads "Start Here!" — so there
  // is no leg to stop 0. Leg i (i ≥ 1) walks stop i-1 → stop i. It used to
  // begin with a fixed start point in the Atrium and a leg from there to stop
  // 1; `resolveAtrium` now serves Free Roam only.
  useEffect(() => {
    if (stage !== "touring" || !activeTour || !mapReady || !navReady || !navGraphRef.current) return;
    const mode = accessibleMode ? "a11y" : "std";
    const cacheKey = `${activeTour.id}|${mode}|${routesSig(activeTour.routes)}`;
    if (tourLegsBuiltForTourRef.current === cacheKey) return; // already built

    let cancelled = false;
    (async () => {
      clearRoute();
      stopDoorNodeRef.current = {};
      const stops = activeTour.stops;
      const nodeById = new Map(navGraphRef.current.nodes.map(n => [n.id, n]));

      // Resolve nav entries for each stop (null if not in graph or fallback)
      const stopEntries = stops.map(s => entryFor(s));

      // Build legs: leg i goes from anchor[i] → stopEntries[i].
      // anchor[1] = ANY door of the first stop (the visitor is standing there);
      // anchor[i>1] = the EXACT door we arrived at on the previous leg, so a
      // multi-door room uses the same door for arrival and departure (no
      // walking out a different door than you came in). The first stop's pin
      // goes on the door the first leg leaves from.
      let lastAnchor = stopEntries[0] || null;
      // The door the first leg leaves stop 0 by — its pin goes there.
      const leftFrom = (i, nodeId) => { if (i === 1 && nodeId && stopDoorNodeRef.current[0] == null) stopDoorNodeRef.current[0] = nodeId; };
      for (let i = 1; i < stops.length; i++) {
        if (cancelled) return;
        const dest = stopEntries[i];
        if (!dest) continue; // no nav data — skip leg
        if (!lastAnchor) { lastAnchor = dest; continue; } // nothing routable to walk from yet

        // The editor's drawing for this leg, if there is one we can still walk.
        const plan = planDrawnLeg(storedRoute(activeTour, i, mode), nodeById, dest.node_ids, lastAnchor.node_ids);
        if (plan) {
          const { ids } = plan;
          let nodes = ids.map(id => nodeById.get(id));
          if (plan.needsConnector) {
            const first = nodeById.get(ids[0]);
            const connector = await findBestPath(
              lastAnchor,
              { ...dest, node_ids: [ids[0]], floors: new Set(Number.isFinite(first.floor) ? [first.floor] : []) },
              accessibleMode,
            );
            if (cancelled) return;
            // The connector ends at ids[0]; drop that so the node is not doubled.
            if (connector) nodes = [...connector.nodes.slice(0, -1), ...nodes];
          }
          drawLegSegments(nodes, i);
          leftFrom(i, nodes[0]?.id);
          const arrivalId = ids[ids.length - 1];
          stopDoorNodeRef.current[i] = arrivalId;
          lastAnchor = { ...dest, node_ids: [arrivalId] };
          continue;
        }

        const path = await findBestPath(lastAnchor, dest, accessibleMode);
        if (cancelled) return;
        if (path) {
          drawLegSegments(path.nodes, i);
          leftFrom(i, path.ids[0]);
          // Lock the arrival door; the pin AND the next leg's departure use it.
          const arrivalId = path.ids[path.ids.length - 1];
          stopDoorNodeRef.current[i] = arrivalId;
          lastAnchor = { ...dest, node_ids: [arrivalId] };
        } else {
          lastAnchor = dest;
        }
      }
      tourLegsBuiltForTourRef.current = cacheKey;

      // Re-place the numbered pins now that each stop's true entry door is known,
      // and start the moving direction arrows on the current leg — the
      // segments only exist now that the async build has finished.
      renderMarkersRef.current();
      restyleRef.current();

      // Fit to the leg being walked (the only one drawn). At the first stop
      // there is none: the stop-change effect has already centred its pin.
      const map = mapInstanceRef.current;
      const shown = routeSegmentsRef.current.filter(seg => seg.legIdx === currentStopIdxRef.current);
      if (map && shown.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        shown.forEach(({ polyline }) => {
          polyline.getPath().forEach(p => bounds.extend(p));
        });
        map.fitBounds(bounds, 80);
      }
    })();

    return () => { cancelled = true; };
  }, [stage, activeTour, mapReady, navReady, accessibleMode, entryFor, findBestPath, drawLegSegments, clearRoute]);

  // Track the last stop we auto-focused so we only force the floor when the
  // stop actually changes — never when the user taps a transition box.
  const lastFocusedStopRef = useRef(-1);

  // ── On stop change: restyle legs, focus current stop, set floor ─────────
  useEffect(() => {
    if (stage !== "touring" || !activeTour) {
      lastFocusedStopRef.current = -1;
      return;
    }
    restyleRef.current();

    const stop = activeTour.stops[currentStopIdx];
    if (!stop) return;
    const dest = entryFor(stop);
    highlightDestRoom(dest?.room_number || stop.room, dest?.building || stop.building);
    renderTourMarkers();

    // Only pan / force the floor when the stop index actually changed.
    // Center on the DEPARTURE spot for this leg (where the user currently is —
    // the previous stop, at the door they arrived by), not the next stop. At
    // the first stop they are standing at the stop itself ("Start Here!").
    if (lastFocusedStopRef.current !== currentStopIdx) {
      lastFocusedStopRef.current = currentStopIdx;
      const graph = navGraphRef.current;
      // The nearest earlier stop that is on the map — a stop that could not be
      // routed to was never arrived at, so the visitor is still at the one before.
      let depIdx = Math.max(0, currentStopIdx - 1);
      while (depIdx > 0 && !entryFor(activeTour.stops[depIdx])) depIdx--;
      const depEntry = entryFor(activeTour.stops[depIdx]);
      const depDoor = stopDoorNodeRef.current[depIdx];
      const n = (depDoor && graph?.nodes.find(nn => nn.id === depDoor))
             || (depEntry && graph?.nodes.find(nn => depEntry.node_ids.includes(nn.id)))
             || (dest && graph?.nodes.find(nn => dest.node_ids.includes(nn.id)));
      if (n) {
        const map = mapInstanceRef.current;
        if (map) map.panTo({ lat: n.lat, lng: n.lng });
        if (Number.isFinite(n.floor)) setSelectedFloor(n.floor);
      }
    }
  }, [stage, activeTour, currentStopIdx, entryFor, highlightDestRoom, renderTourMarkers]);

  // Re-render markers when stamps change
  useEffect(() => { renderTourMarkers(); }, [stampedStops, renderTourMarkers]);

  // Free-roam info must match the tour exactly: pull the matching tour stop
  // (single source of truth) and surface its rich content.
  const matchingStop = useCallback((loc) => {
    if (!loc || !tourData) return null;
    const lb = normalizeBuilding(loc.building), lr = String(loc.room);
    for (const t of tourData.tours) {
      for (const s of (t.stops || [])) {
        if (normalizeBuilding(s.building) === lb && String(s.room) === lr) return s;
      }
    }
    // Fall back to a label match (handles named rooms like "UMES Lounge")
    for (const t of tourData.tours) {
      for (const s of (t.stops || [])) {
        if (s.label && loc.label && s.label === loc.label) return s;
      }
    }
    return null;
  }, [tourData]);

  // Merge: tour stop content wins; loc fills anything the stop lacks.
  const infoFor = useCallback((loc) => {
    if (!loc) return null;
    const s = matchingStop(loc) || {};
    return {
      building: loc.building, room: loc.room,
      label: s.title || s.label || loc.title || loc.label,
      subtitle: s.subtitle || loc.subtitle || "",
      images: (Array.isArray(s.images) && s.images.length ? s.images
                : Array.isArray(loc.images) ? loc.images : []),
      videoUrl: s.videoUrl || loc.videoUrl || "",
      text: s.text || loc.description || loc.subtitle || "",
    };
  }, [matchingStop]);

  const goToFreeRoamLocation = useCallback((loc) => {
    const dest = entryFor(loc);
    if (!dest) return;
    // Start from the user's current spot (a previously-visited place) if set,
    // otherwise the Atrium.
    let start = resolveAtrium(navGraphRef.current);
    if (freeRoamOrigin) {
      const o = entryFor(freeRoamOrigin);
      if (o) start = o;
    }
    runRoute(start, dest);
    highlightDestRoom(dest.room_number, dest.building);
    setFreeRoamOrigin(loc);        // they've moved to this place → new start point
    setFreeRoamSelection(null);    // close the detail popup
    setFreeRoamListOpen(false);    // close the places-of-interest dropdown
  }, [entryFor, runRoute, highlightDestRoom, freeRoamOrigin]);

  // Entering Free Roam puts you back at the Atrium.
  useEffect(() => { if (stage === "freeroam") setFreeRoamOrigin(null); }, [stage]);

  const clearFreeRoamOverlays = useCallback(() => {
    freeRoamOverlaysRef.current.forEach(({ overlay }) => overlay.setMap(null));
    freeRoamOverlaysRef.current = [];
  }, []);

  // ── Free roam: star pins (name shown on hover + in the list/popup) ───────
  const renderFreeRoamOverlays = useCallback(() => {
    clearStopMarkers();
    clearFreeRoamOverlays();
    const map = mapInstanceRef.current;
    if (!map || !tourData || !navGraphRef.current) return;
    // Zoomed out: only show curated primary landmarks; zoomed in: show all.
    const showAll = mapZoomRef.current >= debugRef.current.primaryZoom;
    tourData.freeRoamLocations.forEach(loc => {
      const entry = entryFor(loc);
      if (!entry) return;
      const n = navGraphRef.current.nodes.find(nn => entry.node_ids.includes(nn.id));
      if (!n) return;
      const visible = showAll || loc.primary;
      const StarCls = FreeRoamStarOverlayRef.current;
      let overlay;
      if (StarCls) {
        overlay = new StarCls({ lat: n.lat, lng: n.lng }, !!loc.primary, () => setFreeRoamSelection(loc));
        overlay.setMap(visible ? map : null);
      } else {
        overlay = new window.google.maps.Marker({
          position: { lat: n.lat, lng: n.lng },
          icon: freeRoamStarIcon(!!loc.primary),
          title: withRoomDash(loc.label || stopCode(loc)),
          map: visible ? map : null,
          zIndex: loc.primary ? 620 : 600,
        });
        overlay.addListener("click", () => setFreeRoamSelection(loc));
      }
      freeRoamOverlaysRef.current.push({ overlay, loc });
    });
  }, [tourData, clearStopMarkers, clearFreeRoamOverlays, entryFor]);

  useEffect(() => {
    if (stage !== "freeroam" || !mapReady) {
      clearFreeRoamOverlays();
      return;
    }
    clearRoute();
    highlightDestRoom(null);
    if (navReady) renderFreeRoamOverlays();
    const map = mapInstanceRef.current;
    if (map) { map.setCenter(EITC_CENTER); map.setZoom(19); }
  }, [stage, mapReady, navReady, renderFreeRoamOverlays, clearRoute, clearFreeRoamOverlays, highlightDestRoom]);

  // Toggle star-pin visibility (only "primary" landmarks show when zoomed out)
  // whenever the zoom crosses the threshold.
  useEffect(() => {
    if (stage !== "freeroam") return;
    const map = mapInstanceRef.current;
    const showAll = mapZoom >= debugSettings.primaryZoom;
    freeRoamOverlaysRef.current.forEach(({ overlay, loc }) => {
      overlay.setMap(showAll || loc.primary ? map : null);
    });
  }, [mapZoom, stage, debugSettings.primaryZoom]);

  // ── Questionnaire / tour selection ───────────────────────────────────────
  const startTour = useCallback((tour) => {
    if (!tourData || !tour) return;
    // Record the chosen tour + start timing/method tracking for the email
    surveyRef.current = { prompt: "Chosen tour", optionId: tour.id, optionLabel: tour.title || tour.id };
    stampMethodRef.current = {};
    tourStartRef.current = Date.now();
    tourEndRef.current = null;
    tourLegsBuiltForTourRef.current = null;
    stopDoorNodeRef.current = {};
    clearRoute();
    setActiveTour(tour);
    setCurrentStopIdx(0);
    setStampedStops([]);
    setStage("touring");
  }, [tourData, clearRoute]);

  // ── Quote submit — verified server-side so the answer never ships ─────────
  const handleQuoteSubmit = useCallback(async () => {
    if (!activeTour || quoteChecking) return;
    const stop = activeTour.stops[currentStopIdx];
    if (!stop) return;
    // Preview: verify against the draft answer client-side (no saved data to
    // hit /api/verify-quote with). No-quote stops accept anything.
    if (isPreview) {
      if (stop.hasQuote === false || normalizeQuote(quoteInput) === normalizeQuote(stop.quote)) {
        setQuoteInput(""); setQuoteWrong(false);
        stampMethodRef.current[currentStopIdx] = "quote";
        setStampedStops(prev => prev.includes(currentStopIdx) ? prev : [...prev, currentStopIdx]);
        setStopPopupOpen(true);
      } else setQuoteWrong(true);
      return;
    }
    // Stops without a quote accept any input (server agrees).
    if (stop.hasQuote === false) {
      setQuoteInput(""); setQuoteWrong(false);
      stampMethodRef.current[currentStopIdx] = "quote";
      setStampedStops(prev => prev.includes(currentStopIdx) ? prev : [...prev, currentStopIdx]);
      setStopPopupOpen(true);
      return;
    }
    setQuoteChecking(true); setQuoteWrong(false);
    try {
      const res = await fetch(`${API_BASE}/api/verify-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenRef.current}` },
        body: JSON.stringify({ tourId: activeTour.id, stopIndex: currentStopIdx, guess: quoteInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setQuoteInput("");
        stampMethodRef.current[currentStopIdx] = "quote";
        setStampedStops(prev => prev.includes(currentStopIdx) ? prev : [...prev, currentStopIdx]);
        setStopPopupOpen(true);
      } else {
        setQuoteWrong(true);
      }
    } catch {
      setQuoteWrong(true);
    } finally {
      setQuoteChecking(false);
    }
  }, [activeTour, currentStopIdx, quoteInput, quoteChecking, isPreview]);

  const handleSkipStamp = useCallback(() => {
    if (!activeTour) return;
    setQuoteInput(""); setQuoteWrong(false);
    stampMethodRef.current[currentStopIdx] = "skip";
    setStampedStops(prev => prev.includes(currentStopIdx) ? prev : [...prev, currentStopIdx]);
    setStopPopupOpen(true);
  }, [activeTour, currentStopIdx]);

  const handleAdvance = useCallback(() => {
    setStopPopupOpen(false);
    if (!activeTour) return;
    const next = currentStopIdx + 1;
    if (next >= activeTour.stops.length) {
      tourEndRef.current = Date.now(); // tour completed
      setStage("complete");
    } else {
      setCurrentStopIdx(next);
    }
  }, [activeTour, currentStopIdx]);

  const handleSubmitContact = useCallback(async () => {
    const fields = introContent(tourData).closing.fields;
    if (!checkAnswers(fields, contactValues).ok || emailStatus === "sending") return;
    const tour = activeTour;
    const completedAt = tourEndRef.current || Date.now();
    const details = (tour?.stops || []).map((s, i) => ({
      n: i + 1,
      label: s.label || s.title || stopCode(s),
      method: stampMethodRef.current[i] || (stampedStops.includes(i) ? "quote" : "missed"),
    }));
    const skipCount = details.filter(d => d.method === "skip").length;
    const { values } = checkAnswers(fields, contactValues);
    const payload = {
      // The built-in fields keep the top-level names the email always read;
      // every answer also travels in `answers`, which is how custom questions
      // arrive. The server labels them from the SAVED form, not from this.
      name: values.name || "",
      email: values.email || "",
      grade: values.grade || "",
      city: values.city || "",
      school: values.school || "",
      answers: values,
      survey: surveyRef.current || {},
      tour: { id: tour?.id, title: tour?.title },
      stamps: { total: tour?.stops.length || 0, earned: stampedStops.length, details },
      skipCount,
      startedAt: tourStartRef.current,
      completedAt,
    };
    // Preview: show the thank-you screen exactly as a visitor sees it, but
    // never POST — the Creator's "Test tour" would otherwise mail a real
    // completion email about a draft to whatever address the editor typed.
    if (isPreview) { setContactSubmitted(true); setEmailStatus("sent"); return; }
    setEmailStatus("sending");
    // Move to the thank-you screen immediately; report email status inline
    setContactSubmitted(true);
    try {
      const res = await fetch(`${API_BASE}/api/send-tour-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setEmailStatus(res.ok ? "sent" : "error");
    } catch {
      setEmailStatus("error");
    }
  }, [tourData, contactValues, activeTour, stampedStops, emailStatus, isPreview]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ position: "fixed", inset: 0, background: UM.navy, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SANS, padding: 20 }}>
        <div style={{ background: UM.cream, borderRadius: 22, overflow: "hidden", maxWidth: 400, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.45)" }}>
          <div style={{ background: UM.navy, padding: "22px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <UMLogo height={42} reverse />
            <div style={{ width: 36, height: 6, background: UM.gold, borderRadius: 3 }} />
          </div>
          <div style={{ padding: "26px 28px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 800, color: UM.brown, lineHeight: 1.15 }}>Engineering Tour</div>
            <div style={{ fontSize: 13, color: "#7a6a58", marginTop: 6, marginBottom: 22 }}>A self-guided walk through EITC — let's get you in.</div>
            <input autoFocus type="password" value={pwInput} disabled={pwLoading || pwRateLimited}
              onChange={e => { setPwInput(e.target.value); setPwError(false); setPwRateLimited(false); }}
              onKeyDown={e => { if (e.key === 'Enter') handlePasswordSubmit(); }}
              placeholder="Enter password"
              style={{ width: "100%", fontSize: 15, padding: "12px 14px", borderRadius: 11, border: `2px solid ${pwError ? "#c0392b" : UM.line}`, outline: "none", boxSizing: "border-box", marginBottom: (pwError || pwRateLimited) ? 8 : 16, background: "#fff", opacity: (pwLoading || pwRateLimited) ? 0.6 : 1 }} />
            {pwError && <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 12 }}>That's not it — try again.</div>}
            {pwRateLimited && <div style={{ fontSize: 12, color: "#b45309", marginBottom: 12 }}>Too many tries — give it a minute.</div>}
            <button onClick={handlePasswordSubmit} disabled={pwLoading || pwRateLimited}
              style={{ width: "100%", fontSize: 15, fontWeight: 800, color: UM.brown, background: UM.gold, border: "none", borderRadius: 11, padding: "13px 0", cursor: (pwLoading || pwRateLimited) ? "not-allowed" : "pointer", opacity: (pwLoading || pwRateLimited) ? 0.7 : 1 }}>
              {pwLoading ? "Checking…" : "Let's go →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalStops = activeTour?.stops.length || 0;
  const stampCount = stampedStops.length;
  // The intro and closing screens' words and the completion form, defaults
  // filled in (src/utils/engtourIntro.js; edited in the creator).
  const intro = introContent(tourData);
  const formFieldsShown = intro.closing.fields.filter(isAsked);
  const formCheck = checkAnswers(intro.closing.fields, contactValues);
  const canSkipStamp = activeTour?.skippableStamps !== false;
  // One tour (with stops) and nothing else in the list: there is nothing to choose, so
  // "Get started" goes straight to the room-numbers screen. Add a second tour (even an
  // empty "coming soon" one) and the picker comes back by itself.
  const allTours = tourData?.tours || [];
  const onlyTour = allTours.length === 1 && (allTours[0].stops || []).length ? allTours[0] : null;
  // Accessibility preference, saved for the session. It lives on the picker; when the
  // picker is skipped it moves to the room-numbers screen so nobody loses it.
  const accessibleToggle = (
    <button
      onClick={() => setAccessibleMode(m => !m)}
      aria-pressed={accessibleMode}
      style={{
        marginTop: 18, width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "13px 14px", borderRadius: 14, cursor: "pointer", textAlign: "left",
        border: `2px solid ${accessibleMode ? UM.gold : UM.line}`,
        background: accessibleMode ? "#fff8e6" : "#fff",
      }}>
      <span style={{
        width: 34, height: 34, borderRadius: 17, flexShrink: 0, fontSize: 17,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: accessibleMode ? UM.gold : UM.sand, color: UM.brown,
      }} aria-hidden>♿</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 800, color: UM.brown }}>Accessible route</span>
        <span style={{ display: "block", fontSize: 12, color: "#8a7a66", marginTop: 2 }}>
          Use elevators and ramps — avoid stairs
        </span>
      </span>
      <span style={{
        width: 44, height: 26, borderRadius: 13, flexShrink: 0, position: "relative",
        background: accessibleMode ? UM.gold : "#d8cdb8", transition: "background 0.15s",
      }}>
        <span style={{
          position: "absolute", top: 3, left: accessibleMode ? 21 : 3,
          width: 20, height: 20, borderRadius: 10, background: "#fff", transition: "left 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        }} />
      </span>
    </button>
  );
  const currentStop = activeTour?.stops[currentStopIdx];
  const nextStop = activeTour?.stops[currentStopIdx + 1] || null;
  const tourComplete = activeTour && stampCount >= totalStops;

  const exitTour = (toFreeRoam) => {
    tourLegsBuiltForTourRef.current = null;
    stopDoorNodeRef.current = {};
    setActiveTour(null); setCurrentStopIdx(0); setStampedStops([]);
    setStopPopupOpen(false); clearRoute(); clearStopMarkers(); highlightDestRoom(null);
    setIntroStep("intro"); setPendingTourId(null);
    setStage(toFreeRoam ? "freeroam" : "questionnaire");
  };

  return (
    <div style={{ position: "fixed", inset: 0, fontFamily: SANS, display: "flex", flexDirection: "column" }}>
      {/* ── UM-branded top bar ── */}
      <div style={{
        flexShrink: 0, background: UM.navy, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "8px 12px" : "10px 18px", gap: 10,
        borderBottom: `4px solid ${UM.gold}`, zIndex: 40,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <UMLogo height={isMobile ? 30 : 38} reverse onClick={handleLogoTap} />
          <div style={{ width: 1, height: 30, background: "rgba(255,255,255,0.25)", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: SERIF, fontSize: isMobile ? 14 : 16, fontWeight: 800, lineHeight: 1.1 }}>Engineering Tour</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {stage === "questionnaire" && "Pick where to start"}
              {stage === "touring" && activeTour && activeTour.title}
              {stage === "complete" && "You did it!"}
              {stage === "freeroam" && "Free roam — explore anything"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, flexShrink: 0 }}>
          {accessibleMode && (
            <span title="Routes avoid stairs (elevators & ramps)"
              style={{
                display: "flex", alignItems: "center", gap: 5,
                color: UM.gold, fontSize: isMobile ? 11 : 12, fontWeight: 700, whiteSpace: "nowrap",
              }}>
              <span aria-hidden>♿</span>
              <span>Accessible mode is on.</span>
            </span>
          )}

          {(tourData?.resources?.length > 0) && (
            <button onClick={() => setResourcesOpen(true)} style={{
              padding: isMobile ? "6px 9px" : "7px 13px", borderRadius: 9, border: `1.5px solid ${UM.gold}`, cursor: "pointer",
              background: "transparent", color: UM.gold, fontSize: isMobile ? 11 : 12, fontWeight: 800, whiteSpace: "nowrap",
            }}>🔗{!isMobile && " Resources"}</button>
          )}
          {stage === "freeroam" && (
            <button onClick={() => exitTour(false)} style={{
              padding: isMobile ? "6px 10px" : "7px 13px", borderRadius: 9, border: `1.5px solid ${UM.gold}`, cursor: "pointer",
              background: "transparent", color: UM.gold, fontSize: isMobile ? 11 : 12, fontWeight: 800, whiteSpace: "nowrap",
            }}>🎒 Tours</button>
          )}
          {stage === "touring" && (
            <>
              <button onClick={() => { if (window.confirm("Skip the rest and explore on your own?")) exitTour(true); }} style={{
                padding: isMobile ? "6px 9px" : "7px 13px", borderRadius: 9, border: `1.5px solid ${UM.gold}`, cursor: "pointer",
                background: "transparent", color: UM.gold, fontSize: isMobile ? 11 : 12, fontWeight: 800, whiteSpace: "nowrap",
              }}>🧭{!isMobile && " Free Roam"}</button>
              <button onClick={() => { if (window.confirm("Exit current tour?")) exitTour(false); }} style={{
                padding: isMobile ? "6px 9px" : "7px 13px", borderRadius: 9, border: "none", cursor: "pointer",
                background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: isMobile ? 11 : 12, fontWeight: 700, whiteSpace: "nowrap",
              }}>✕{!isMobile && " Exit"}</button>
            </>
          )}
        </div>
      </div>

      {/* Map area */}
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
      <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />

      {/* Debug panel — unlocked by tapping the header logo 10× (within 1.5s).
          Lets you read live values and dial in the thresholds, then tell Claude
          what numbers you landed on. */}
      {debugMode && (
        <div style={{
          position: "absolute", top: isMobile ? 56 : 64, left: 12, zIndex: 40,
          width: isMobile ? "calc(100vw - 24px)" : 320, maxWidth: 360,
          background: "rgba(15,25,45,0.96)", color: "#fff",
          borderRadius: 12, padding: "12px 14px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.55)", border: `1.5px solid ${UM.gold}`,
          fontFamily: SANS, fontSize: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 14 }}>🛠</span>
              <span style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 800, color: UM.gold }}>Debug</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>tap logo 10× to toggle</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setDebugSettings(DEFAULT_DEBUG)}
                style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700 }}>
                Reset
              </button>
              <button onClick={() => setDebugMode(false)}
                style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "none", cursor: "pointer", width: 22, height: 22, borderRadius: 6, fontSize: 12, fontWeight: 800 }}>✕</button>
            </div>
          </div>

          {/* Live read-outs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: 6 }}>
              <span style={{ color: "rgba(255,255,255,0.6)" }}>zoom </span>
              <span style={{ color: UM.gold, fontWeight: 800 }}>{Number(mapZoom).toFixed(2)}</span>
            </span>
            <span style={{ background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: 6 }}>
              <span style={{ color: "rgba(255,255,255,0.6)" }}>floor </span>
              <span style={{ color: UM.gold, fontWeight: 800 }}>{selectedFloor} → {levelNumber(selectedFloor)}</span>
            </span>
            <span style={{ background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: 6 }}>
              <span style={{ color: "rgba(255,255,255,0.6)" }}>stage </span>
              <span style={{ color: UM.gold, fontWeight: 800 }}>{stage}</span>
            </span>
          </div>

          {/* Sliders */}
          {[
            { key: "primaryZoom",       label: "Star pins appear at zoom ≥",       min: 15,  max: 22,  step: 0.5 },
            { key: "stairElevatorZoom", label: "Stair / elevator icons at zoom ≥", min: 15, max: 22, step: 0.5 },
          ].map(({ key, label, min, max, step }) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, color: "rgba(255,255,255,0.85)" }}>
                <span>{label}</span>
                <span style={{ color: UM.gold, fontWeight: 800 }}>{debugSettings[key]}</span>
              </div>
              <input type="range" min={min} max={max} step={step}
                value={debugSettings[key]}
                onChange={e => setDebugSettings(s => ({ ...s, [key]: Number(e.target.value) }))}
                style={{ width: "100%", accentColor: UM.gold }} />
            </div>
          ))}
        </div>
      )}

      {/* The floor-level indicator now sits right on top of the stamp box
          (see the bottom quote box below). */}

      {/* Floating vertical floor selector (right edge) — free roam only.
          During a tour the floor follows the current stop / transition boxes. */}
      {stage === "freeroam" && (
        <div style={{
          position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 25,
          display: "flex", flexDirection: "column", gap: 4,
          background: "rgba(27,51,94,0.88)", borderRadius: 12, padding: 5,
          boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
        }}>
          {[6, 5, 4, 3, 2, 1].map(f => (
            <button key={f} onClick={() => setSelectedFloor(f)} title={levelLabel(f)} style={{
              width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: 9, border: "none", cursor: "pointer",
              fontSize: isMobile ? 13 : 14, fontWeight: 800,
              background: selectedFloor === f ? UM.gold : "rgba(255,255,255,0.12)",
              color: selectedFloor === f ? UM.brown : "rgba(255,255,255,0.8)",
            }}>{f}</button>
          ))}
        </div>
      )}

      {/* ── Welcome + questionnaire ─────────────────────────────── */}
      {stage === "questionnaire" && tourData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(27,51,94,0.65)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div style={{ background: UM.cream, borderRadius: 22, maxWidth: 460, width: "100%", boxShadow: "0 20px 64px rgba(0,0,0,0.5)", overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            {/* Big banner */}
            <div style={{ position: "relative", height: 170, background: `linear-gradient(135deg, ${UM.navy} 0%, ${UM.blue} 55%, ${UM.sky} 100%)`, flexShrink: 0 }}>
              {intro.banner && (
                <img src={intro.banner} alt=""
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  onError={e => { e.target.style.display = "none"; }} />
              )}
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(27,51,94,0.1) 0%, rgba(27,51,94,0.78) 100%)" }} />
              <div style={{ position: "absolute", left: 20, top: 16 }}><UMLogo height={34} reverse /></div>
              <div style={{ position: "absolute", left: 20, right: 20, bottom: 14 }}>
                <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
                  {intro.heading}
                </div>
              </div>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 5, background: UM.gold }} />
            </div>

            <div style={{ padding: "20px 24px 24px", overflowY: "auto" }}>
              {/* ── Step 1: intro ── */}
              {introStep === "intro" && (
                <>
                  <div style={{ fontSize: 14, color: UM.ink, marginBottom: 20, lineHeight: 1.55 }}>
                    {intro.blurb}
                  </div>
                  <button onClick={() => { if (onlyTour) { setPendingTourId(onlyTour.id); setIntroStep("howto"); } else setIntroStep("pick"); }} style={{
                    width: "100%", padding: "14px 0", borderRadius: 13, border: "none",
                    background: UM.navy, color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer",
                  }}>Get started →</button>
                </>
              )}

              {/* ── Step 2: pick a tour (every tour built in the Tour Creator) ── */}
              {introStep === "pick" && (
                <>
                  <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 800, color: UM.brown, marginBottom: 4 }}>Choose your tour</div>
                  <div style={{ fontSize: 13, color: "#8a7a66", marginBottom: 14 }}>Pick one to get started — you can switch later.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {tourData.tours.map((tour, i) => {
                      const empty = (tour.stops || []).length === 0;
                      return (
                        <button key={tour.id} onClick={() => { if (empty) return; setPendingTourId(tour.id); setIntroStep("howto"); }} disabled={empty} style={{
                          padding: "14px 16px", borderRadius: 14, border: `2px solid ${UM.line}`,
                          cursor: empty ? "not-allowed" : "pointer", opacity: empty ? 0.55 : 1,
                          background: "#fff", color: UM.brown, textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                          transition: "border-color 0.15s",
                        }}
                          onMouseEnter={e => { if (!empty) e.currentTarget.style.borderColor = UM.gold; }}
                          onMouseLeave={e => e.currentTarget.style.borderColor = UM.line}>
                          <span style={{ width: 34, height: 34, borderRadius: 17, background: UM.gold, color: UM.brown, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 15, flexShrink: 0 }}>{i + 1}</span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "block", fontSize: 15, fontWeight: 800 }}>{tour.title || `Tour ${tour.id}`}</span>
                            {(tour.subtitle || empty) && <span style={{ display: "block", fontSize: 12, color: "#8a7a66", marginTop: 2 }}>{tour.subtitle || ""}{empty ? (tour.subtitle ? " · " : "") + "coming soon" : ""}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {accessibleToggle}
                </>
              )}

              {/* ── Step 3: how to read room numbers, then start ── */}
              {introStep === "howto" && (
                <>
                  <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 800, color: UM.brown, marginBottom: 12 }}>{intro.roomGuide.heading}</div>
                  <div style={{ background: UM.sand, borderRadius: 14, padding: "13px 14px 12px", marginBottom: 20, borderLeft: `4px solid ${UM.gold}` }}>
                    <RoomGuideList body={intro.roomGuide.body} />
                  </div>
                  {onlyTour && <div style={{ margin: "-6px 0 18px" }}>{accessibleToggle}</div>}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setIntroStep(onlyTour ? "intro" : "pick")} style={{
                      flex: "0 0 auto", padding: "14px 18px", borderRadius: 13, border: `2px solid ${UM.line}`,
                      background: "#fff", color: UM.brown, fontSize: 14, fontWeight: 800, cursor: "pointer",
                    }}>← Back</button>
                    <button onClick={() => startTour(tourData.tours.find(t => t.id === pendingTourId))} style={{
                      flex: 1, padding: "14px 0", borderRadius: 13, border: "none",
                      background: UM.navy, color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer",
                    }}>Start tour →</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Touring: small "where you're headed" info box ───────── */}
      {stage === "touring" && currentStop && !stopPopupOpen && !tourComplete && (
        <div style={{
          position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 30,
          width: isMobile ? "calc(100vw - 24px)" : 460,
          background: UM.cream, borderRadius: 14, padding: "10px 14px",
          boxShadow: "0 6px 22px rgba(0,0,0,0.35)", borderLeft: `5px solid ${UM.gold}`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ width: 30, height: 30, borderRadius: 15, background: UM.sky, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14, flexShrink: 0 }}>
            {currentStopIdx + 1}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: UM.sky }}>{currentStopIdx === 0 ? "START HERE!" : "NEXT — HEAD HERE"}</div>
            <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800, color: UM.brown, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {withRoomDash(currentStop.title || currentStop.label)}
            </div>
            {currentStop.subtitle && (
              <div style={{ fontSize: 12, color: "#7a6a58", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentStop.subtitle}</div>
            )}
          </div>
        </div>
      )}

      {/* ── Touring: bottom quote box + stamp counter ───────────── */}
      {stage === "touring" && currentStop && !stopPopupOpen && !tourComplete && (
        <div style={{
          position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 30,
          width: isMobile ? "calc(100vw - 24px)" : 500,
          background: UM.navy, borderRadius: 18, padding: "14px 14px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.5)", borderTop: `4px solid ${UM.gold}`,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {/* Floor-level indicator — sits right on top of the stamp box */}
          <div style={{
            position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)",
            marginBottom: 8, zIndex: 1,
            background: "rgba(27,51,94,0.95)", color: "#fff",
            padding: "6px 14px", borderRadius: 11,
            boxShadow: "0 4px 18px rgba(0,0,0,0.45)", borderLeft: `4px solid ${UM.gold}`,
            display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", pointerEvents: "none",
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "rgba(255,255,255,0.7)" }}>YOU'RE ON</span>
            <span style={{ fontFamily: SERIF, fontSize: isMobile ? 16 : 18, fontWeight: 900, lineHeight: 1, color: UM.gold }}>
              {levelLabel(selectedFloor)}
            </span>
          </div>
          <div style={{ fontSize: 12, color: UM.gold, fontWeight: 800 }}>
            🔎 Find the quote posted on the door and type it in to earn your stamp
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <input
                value={quoteInput}
                onChange={e => { setQuoteInput(e.target.value); setQuoteWrong(false); }}
                onKeyDown={e => { if (e.key === 'Enter') handleQuoteSubmit(); }}
                placeholder="Stamp"
                style={{
                  fontSize: 15, padding: "11px 12px", borderRadius: 11, outline: "none",
                  border: `2px solid ${quoteWrong ? "#e57373" : "transparent"}`,
                  background: "#fff", color: UM.ink, fontWeight: 600,
                  width: "100%", boxSizing: "border-box",
                }}
              />
              {quoteWrong && <div style={{ fontSize: 11, color: "#ffcdd2", fontWeight: 600 }}>Not quite — check the door sign.</div>}
            </div>
            <button onClick={handleQuoteSubmit} disabled={quoteChecking} style={{
              padding: "0 16px", borderRadius: 11, border: "none", cursor: quoteChecking ? "default" : "pointer",
              background: UM.gold, color: UM.brown, fontWeight: 900, fontSize: 13, whiteSpace: "nowrap",
              opacity: quoteChecking ? 0.6 : 1,
            }}>{quoteChecking ? "…" : "Stamp ✓"}</button>
            {canSkipStamp && <button onClick={handleSkipStamp} title="Skip — count as stamped"
              style={{
                padding: "0 12px", borderRadius: 11, border: "1.5px solid rgba(255,255,255,0.3)", cursor: "pointer",
                background: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 700, fontSize: 12, whiteSpace: "nowrap",
              }}>Skip ›</button>}
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              minWidth: 56, padding: "0 8px",
              background: "rgba(255,255,255,0.1)", borderRadius: 11,
            }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.6)", fontWeight: 800, letterSpacing: "0.06em" }}>STAMPS</div>
              <div style={{ fontSize: 19, color: "#7BD389", fontWeight: 900, lineHeight: 1 }}>{stampCount}<span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>/{totalStops}</span></div>
            </div>
          </div>
          {/* Stamp dots */}
          <div style={{ display: "flex", gap: 6 }}>
            {activeTour.stops.map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 6, borderRadius: 3,
                background: stampedStops.includes(i) ? UM.success : "rgba(255,255,255,0.2)",
              }} />
            ))}
          </div>
        </div>
      )}

      {/* ── Stop popup with info ────────────────────────────────── */}
      {stage === "touring" && stopPopupOpen && currentStop && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(27,51,94,0.6)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div style={{ background: UM.cream, borderRadius: 20, maxWidth: 470, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.55)", overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            {/* Images at the top */}
            {Array.isArray(currentStop.images) && currentStop.images.length > 0 && (
              <div style={{ display: "flex", overflowX: "auto", gap: 0, flexShrink: 0, background: "#000", scrollSnapType: "x mandatory" }}>
                {currentStop.images.map((src, idx) => (
                  <img key={idx} src={src} alt="" style={{ width: "100%", height: 200, objectFit: "cover", flexShrink: 0, scrollSnapAlign: "start" }}
                    onError={e => { e.target.style.display = "none"; }} />
                ))}
              </div>
            )}
            <div style={{ background: UM.success, padding: "11px 18px", color: "#fff", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: "#fff", color: UM.success, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14 }}>✓</div>
              <div style={{ flex: 1, fontWeight: 900, fontSize: 14 }}>Stamp earned!</div>
              <div style={{ fontSize: 13, fontWeight: 800 }}>{stampCount}/{totalStops}</div>
            </div>
            <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1, minHeight: 0, WebkitOverflowScrolling: "touch" }}>
              <div style={{ fontSize: 11, color: UM.sky, fontWeight: 800, letterSpacing: "0.06em" }}>
                STOP {currentStopIdx + 1} OF {totalStops}
              </div>
              <div style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 800, color: UM.brown, margin: "3px 0 4px" }}>{withRoomDash(currentStop.title || currentStop.label)}</div>
              {currentStop.subtitle && (
                <div style={{ fontSize: 13, color: "#8a7a66", marginBottom: 12 }}>{currentStop.subtitle}</div>
              )}
              {currentStop.videoUrl && (
                <div style={{ margin: "10px 0 14px", borderRadius: 12, overflow: "hidden", background: "#000" }}>
                  <VideoEmbed url={currentStop.videoUrl} />
                </div>
              )}
              <RichText text={currentStop.text} />

              {/* Conversational nav guidance to the NEXT stop (after stamping).
                  Uses the current stop's `guidance` field if set; otherwise builds
                  a friendly default from the next stop's name and floor. */}
              {currentStopIdx + 1 < totalStops && (() => {
                const next = activeTour.stops[currentStopIdx + 1];
                const nextLabel = withRoomDash(next?.title || next?.label || "the next stop");
                let nextFloor = null;
                const nextDoorId = stopDoorNodeRef.current[currentStopIdx + 1];
                if (nextDoorId && navGraphRef.current) {
                  const nd = navGraphRef.current.nodes.find(nn => nn.id === nextDoorId);
                  if (nd && Number.isFinite(nd.floor)) nextFloor = nd.floor;
                }
                const fallback = nextFloor != null
                  ? `Nice work! Next, make your way to ${nextLabel} on ${levelLabel(nextFloor)}. Use the stairs or elevator if you need to change floors — the route on the map will show you the way.`
                  : `Nice work! Next, make your way to ${nextLabel}. The route on the map will show you the way.`;
                const text = currentStop.guidance || fallback;
                return (
                  <div style={{
                    marginTop: 16, padding: "12px 14px", borderRadius: 12,
                    background: "#fff", border: `1.5px solid ${UM.line}`,
                    borderLeft: `4px solid ${UM.sky}`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: UM.sky, marginBottom: 4 }}>
                      👉 WHAT'S NEXT
                    </div>
                    <div style={{ fontSize: 13, color: UM.ink, lineHeight: 1.55 }}>{text}</div>
                  </div>
                );
              })()}
            </div>
            <div style={{ flexShrink: 0, padding: "12px 22px 16px", borderTop: `1px solid ${UM.line}`, background: UM.cream }}>
              <button onClick={handleAdvance} style={{
                width: "100%", padding: "13px 16px", borderRadius: 12, border: "none", cursor: "pointer",
                background: UM.navy, color: "#fff", fontWeight: 800, fontSize: 14,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {currentStopIdx + 1 >= totalStops ? "Finish tour →" : `Next: ${withRoomDash(nextStop?.title || nextStop?.label || "continue")} →`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Completion / contact form ───────────────────────────── */}
      {stage === "complete" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(27,51,94,0.65)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          {/* Scrolls inside rather than off the screen: the tour's editors can add questions. */}
          <div style={{ background: UM.cream, borderRadius: 20, maxWidth: 420, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.55)", overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: UM.navy, padding: "20px 24px", borderBottom: `4px solid ${UM.gold}`, flexShrink: 0 }}>
              <UMLogo height={32} reverse />
            </div>
            <div style={{ padding: "24px 24px 26px", overflowY: "auto" }}>
            {!contactSubmitted ? (
              <>
                <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, color: UM.brown, marginBottom: 6 }}>{fillStamps(intro.closing.heading, totalStops)}</div>
                <div style={{ fontSize: 14, color: UM.ink, marginBottom: 20, lineHeight: 1.55, whiteSpace: "pre-line" }}>
                  {fillStamps(intro.closing.body, totalStops)}
                </div>
                {formFieldsShown.map(f => (
                  <div key={f.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#8a7a66", marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      {f.label}{f.mode === "optional" && <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0 }}> (optional)</span>}
                    </div>
                    <input type={f.type === "email" ? "email" : "text"} value={contactValues[f.id] || ""} placeholder={f.placeholder || ""} maxLength={f.max}
                      onChange={e => { const v = e.target.value; setContactValues(prev => ({ ...prev, [f.id]: v })); }}
                      style={{ width: "100%", boxSizing: "border-box", fontSize: 15, padding: "11px 13px", border: `2px solid ${UM.line}`, borderRadius: 11, outline: "none", background: "#fff" }} />
                  </div>
                ))}
                {formCheck.badEmail && <div style={{ fontSize: 12, color: "#b45309", margin: "-4px 0 12px" }}>That email address doesn't look right.</div>}
                <button onClick={handleSubmitContact} disabled={!formCheck.ok || emailStatus === "sending"} style={{
                  width: "100%", marginTop: 6, padding: "13px 0", borderRadius: 12, border: "none",
                  background: formCheck.ok ? UM.gold : "#d8cdb8",
                  color: UM.brown, fontWeight: 900, fontSize: 15, cursor: formCheck.ok ? "pointer" : "default",
                }}>{emailStatus === "sending" ? "Submitting…" : "Submit"}</button>
                <button onClick={() => exitTour(true)} style={{
                  width: "100%", marginTop: 10, padding: "11px 0", borderRadius: 12, border: `2px solid ${UM.line}`,
                  background: "none", color: "#8a7a66", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>Skip — just let me explore</button>
              </>
            ) : (
              <>
                <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, color: UM.brown, marginBottom: 6 }}>
                  {formCheck.values.name ? `Thanks, ${formCheck.values.name.split(/\s+/)[0]}!` : "Thanks!"}
                </div>
                <div style={{ fontSize: 14, color: UM.ink, marginBottom: 14, lineHeight: 1.55, whiteSpace: "pre-line" }}>
                  {fillStamps(intro.closing.thanks, totalStops)}
                </div>
                {emailStatus === "error" && (
                  <div style={{ fontSize: 12, color: "#b45309", marginBottom: 14 }}>
                    (We couldn't send the confirmation email, but your info was recorded.)
                  </div>
                )}
                <button onClick={() => exitTour(true)} style={{
                  width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                  background: UM.navy, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer",
                }}>🧭 Start Free Roam</button>
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* ── Free roam: top drop-down list of places of interest ────── */}
      {stage === "freeroam" && tourData && (
        <>
          {/* Toggle (when collapsed) — bottom-left so it doesn't block the
              region underneath where the dropdown unrolls. */}
          {!freeRoamListOpen && (
            <button onClick={() => setFreeRoamListOpen(true)} style={{
              position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 35,
              padding: "10px 14px", borderRadius: 11, border: "none", cursor: "pointer",
              background: UM.navy, color: "#fff", fontWeight: 800, fontSize: 13,
              boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
            }}>▼ Places of interest</button>
          )}

          {/* Always mounted so the slide-down transform animates cleanly.
              Horizontally centered on the page. */}
          <div style={{
            position: "absolute", top: 0,
            left: "50%",
            width: isMobile ? "100%" : 380, maxWidth: isMobile ? "100%" : 420,
            maxHeight: "72vh",
            zIndex: 35,
            background: UM.cream,
            borderBottomLeftRadius: 18,
            borderBottomRightRadius: 18,
            boxShadow: freeRoamListOpen ? "0 12px 32px rgba(0,0,0,0.35)" : "none",
            display: "flex", flexDirection: "column",
            transform: freeRoamListOpen ? "translate(-50%, 0)" : "translate(-50%, -100%)",
            opacity: freeRoamListOpen ? 1 : 0,
            // Swift, fluid ease-out (quint) — quick to settle, no rubber-banding.
            transition: "transform 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease-out",
            transformOrigin: "top center",
            pointerEvents: freeRoamListOpen ? "auto" : "none",
            willChange: "transform, opacity",
          }}>
              <div style={{ background: UM.navy, color: "#fff", padding: "14px 16px 12px", borderBottom: `4px solid ${UM.gold}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 800 }}>Places of Interest</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Tap a place for details</div>
                  </div>
                  <button onClick={() => setFreeRoamListOpen(false)} aria-label="Close places list" style={{
                    background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer",
                    width: 30, height: 30, borderRadius: 8, fontSize: 14, fontWeight: 800, flexShrink: 0,
                  }}>▲</button>
                </div>
                {/* Search — indexes titles & subtitles */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, background: "rgba(255,255,255,0.14)", borderRadius: 10, padding: "8px 11px" }}>
                  <span style={{ fontSize: 14, opacity: 0.8 }} aria-hidden>🔍</span>
                  <input
                    value={freeRoamSearch}
                    onChange={e => { setFreeRoamSearch(e.target.value); if (e.target.value) setFreeRoamFilter(null); }}
                    placeholder="Search places…"
                    style={{
                      flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                      color: "#fff", fontSize: 14, fontWeight: 600,
                    }}
                  />
                  {freeRoamSearch && (
                    <button onClick={() => setFreeRoamSearch("")} aria-label="Clear search" style={{
                      background: "none", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer",
                      fontSize: 15, lineHeight: 1, padding: 0,
                    }}>✕</button>
                  )}
                </div>
                {/* One button per group/type */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                  {(() => {
                    // All departments are always offered as filters, plus "All".
                    const chip = (active, label, onClick) => (
                      <button key={label} onClick={onClick} style={{
                        padding: "6px 11px", borderRadius: 999, cursor: "pointer",
                        border: `1.5px solid ${active ? UM.gold : "rgba(255,255,255,0.3)"}`,
                        background: active ? UM.gold : "transparent",
                        color: active ? UM.brown : "#fff", fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap",
                      }}>{label}</button>
                    );
                    return [
                      chip(freeRoamFilter === null, "All", () => setFreeRoamFilter(null)),
                      ...DEPARTMENTS.map(d => chip(freeRoamFilter === d, d, () => setFreeRoamFilter(d))),
                    ];
                  })()}
                </div>
              </div>
              <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "8px" }}>
                {(() => {
                  const q = freeRoamSearch.trim().toLowerCase();
                  const results = (tourData.freeRoamLocations || [])
                    .filter(loc => freeRoamFilter === null || (loc.departments || []).includes(freeRoamFilter))
                    .filter(loc => {
                      if (!q) return true;
                      const info = infoFor(loc);
                      return `${info.label || ""} ${info.subtitle || ""}`.toLowerCase().includes(q);
                    });
                  if (results.length === 0) {
                    return <div style={{ padding: "24px 12px", textAlign: "center", color: "#8a7a66", fontSize: 13 }}>No places match “{freeRoamSearch}”.</div>;
                  }
                  return results.map((loc, i) => (
                    // Whole card opens the info popup; the map is not moved here —
                    // only the popup's "Route me there" button moves the map.
                    <div key={i} onClick={() => setFreeRoamSelection(loc)} style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "12px 12px",
                      background: "#fff", borderRadius: 13, marginBottom: 8, cursor: "pointer",
                      border: `1.5px solid ${UM.line}`,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 800, color: UM.brown, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{withRoomDash(infoFor(loc).label)}</div>
                        <div style={{ fontSize: 12, color: "#8a7a66", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{loc.subtitle || loc.description}</div>
                      </div>
                      <span style={{ flexShrink: 0, color: UM.sky, fontWeight: 900, fontSize: 18 }} aria-hidden>›</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
        </>
      )}

      {/* ── Free roam: "you are here" indicator (bottom) ─────────── */}
      {stage === "freeroam" && (
        <div style={{
          position: "fixed", left: "50%", bottom: 18, transform: "translateX(-50%)", zIndex: 45,
          background: UM.navy, color: "#fff", borderRadius: 999, padding: freeRoamOrigin ? "9px 8px 9px 15px" : "9px 15px",
          display: "flex", alignItems: "center", gap: 10, boxShadow: "0 6px 22px rgba(0,0,0,0.4)",
          fontSize: 13, maxWidth: "calc(100vw - 28px)",
        }}>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <span style={{ opacity: 0.82 }}>📍 You are at </span>
            <b>{freeRoamOrigin ? withRoomDash(infoFor(freeRoamOrigin).label || stopCode(freeRoamOrigin)) : "the Atrium"}</b>
          </span>
          {freeRoamOrigin && (
            <button onClick={() => setFreeRoamOrigin(null)} title="Reset your start to the Atrium" aria-label="Reset to the Atrium" style={{
              flexShrink: 0, width: 24, height: 24, borderRadius: 12, border: "none",
              background: "rgba(255,255,255,0.22)", color: "#fff", cursor: "pointer", fontWeight: 800, fontSize: 14, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>×</button>
          )}
        </div>
      )}

      {/* ── Free roam: full info popup ──────────────────────────── */}
      {stage === "freeroam" && freeRoamSelection && (() => {
        const info = infoFor(freeRoamSelection);
        return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(27,51,94,0.6)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }} onClick={() => setFreeRoamSelection(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: UM.cream, borderRadius: 20, maxWidth: 460, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.55)", overflow: "hidden", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            {/* Images at the top — same as the tour */}
            {Array.isArray(info.images) && info.images.length > 0 && (
              <div style={{ display: "flex", overflowX: "auto", background: "#000", flexShrink: 0, scrollSnapType: "x mandatory" }}>
                {info.images.map((src, idx) => (
                  <img key={idx} src={src} alt="" style={{ width: "100%", height: 200, objectFit: "cover", flexShrink: 0, scrollSnapAlign: "start" }}
                    onError={e => { e.target.style.display = "none"; }} />
                ))}
              </div>
            )}
            <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1, minHeight: 0, WebkitOverflowScrolling: "touch" }}>
              <div style={{ fontSize: 11, color: UM.sky, fontWeight: 800, letterSpacing: "0.06em" }}>
                {stopCode(freeRoamSelection)}
              </div>
              <div style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 800, color: UM.brown, margin: "3px 0 4px" }}>{withRoomDash(info.label)}</div>
              {info.subtitle && (
                <div style={{ fontSize: 13, color: "#8a7a66", marginBottom: 12 }}>{info.subtitle}</div>
              )}
              {info.videoUrl && (
                <div style={{ margin: "10px 0 14px", borderRadius: 12, overflow: "hidden", background: "#000" }}>
                  <VideoEmbed url={info.videoUrl} />
                </div>
              )}
              <RichText text={info.text} />
            </div>
            <div style={{ flexShrink: 0, padding: "12px 22px 16px", borderTop: `1px solid ${UM.line}`, background: UM.cream }}>
              <button onClick={() => goToFreeRoamLocation(freeRoamSelection)} style={{
                width: "100%", padding: "12px 0", borderRadius: 12, border: "none",
                background: UM.gold, color: UM.brown, fontWeight: 900, fontSize: 14, cursor: "pointer", marginBottom: 8,
              }}>🧭 Route me there</button>
              <button onClick={() => setFreeRoamSelection(null)} style={{
                width: "100%", padding: "10px 0", borderRadius: 12, border: `2px solid ${UM.line}`,
                background: "none", color: "#8a7a66", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>Close</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Resources modal ── */}
      {resourcesOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(27,51,94,0.6)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }} onClick={() => setResourcesOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: UM.cream, borderRadius: 20, maxWidth: 460, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.55)", overflow: "hidden", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: UM.navy, color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `4px solid ${UM.gold}`, flexShrink: 0, gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <UMLogo height={32} reverse />
                <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.25)", flexShrink: 0 }} />
                <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 800 }}>Resources</div>
              </div>
              <button onClick={() => setResourcesOpen(false)} style={{
                background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer",
                width: 30, height: 30, borderRadius: 8, fontSize: 16, fontWeight: 800, flexShrink: 0,
              }}>✕</button>
            </div>
            <div style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
              {(tourData?.resources || []).map((r, i) => (
                <a key={i} href={safeHref(r.url) || undefined} target="_blank" rel="noopener noreferrer" style={{
                  display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
                  textDecoration: "none", padding: "15px 16px", borderRadius: 14,
                  background: "#fff", border: `1.5px solid ${UM.line}`, color: UM.brown,
                }}>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>{r.title}</span>
                    {r.description && (
                      <span style={{ display: "block", fontSize: 12.5, color: "#8a7a66", marginTop: 4, lineHeight: 1.45 }}>{r.description}</span>
                    )}
                  </span>
                  <span style={{ color: UM.sky, fontWeight: 900, fontSize: 16, flexShrink: 0, marginTop: 2 }}>↗</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
